// src/pages/dashboard/ProjectBoardPage.js
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { highlightMentions } from '../../utils/sanitizeContent';
import {
  taskAPI,
  projectAPI,
  commentAPI,
  attachmentAPI,
  timeEntryAPI,
  teamMemberAPI,
  statusAPI,
  sprintAPI,
  clientAPI,
  userAPI,
} from "../../services/api";
import { MentionsInput, Mention } from "react-mentions";
import { toast } from "react-toastify";
import {
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormHelperText,
  CircularProgress,
  Grid,
  Stack,
  Collapse,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Input,
} from "@mui/material";
import {
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  AttachFile as AttachFileIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  LockOutlined as LockIcon,
  Flag as FlagIcon,
  Task as TaskIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "../../context/AuthContext";
import realtime from "../../hooks/RealtimePoll";
import BurndownChart from "../../components/charts/BurndownChart";
import ProjectFormDialog from './ProjectComponents/ProjectFormDialog';

import {
  convertToLocalInput,
  convertToCompanyTime,
  convertLocalToUTCStrict,
} from "../../utils/time";

import { validateFile, ACCEPTED_MIME_TYPES } from '../../utils/boardHelpers';

// ============ SKELETON COMPONENTS ============
const TaskCardSkeleton = () => (
  <Paper sx={{ borderRadius: 2, boxShadow: 2, p: 2, mb: 2, minHeight: 120 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 1,
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Skeleton variant="text" width="70%" height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="90%" height={20} />
      </Box>
      <Skeleton variant="rectangular" width={36} height={24} />
    </Box>
    <Grid container spacing={1} sx={{ mb: 2 }}>
      {[1, 2, 3].map((item) => (
        <Grid item xs={4} key={item}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Skeleton variant="circular" width={16} height={16} />
            <Skeleton variant="text" width="70%" height={16} />
          </Box>
        </Grid>
      ))}
    </Grid>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} variant="circular" width={24} height={24} />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Skeleton variant="rectangular" width={50} height={20} />
        <Skeleton variant="rectangular" width={50} height={20} />
      </Box>
    </Box>
  </Paper>
);

const ColumnSkeleton = () => (
  <Paper
    sx={{
      minWidth: 280,
      maxWidth: 280,
      p: 1.5,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.5,
        pb: 1,
        borderBottom: "2px solid",
        borderColor: "grey.300",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="circular" width={10} height={10} />
        <Skeleton variant="text" width={80} height={20} />
        <Skeleton variant="rectangular" width={24} height={20} />
      </Box>
      <Skeleton variant="circular" width={24} height={24} />
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {[1, 2, 3].map((item) => (
        <TaskCardSkeleton key={item} />
      ))}
    </Box>
  </Paper>
);

// ============ UTILS ============
const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;
  const baseUrl =
    process.env.REACT_APP_AVATAR_URL?.replace(/\/+$/, "") ||
    "http://localhost:8000/storage";
  const cleanPath = avatarPath.replace(/^\/+/, "");
  return `${baseUrl}/${cleanPath}?t=${Date.now()}`;
};

const getAttachmentUrl = (attachment) => {
  if (!attachment) return null;
  const filePath =
    attachment.file_path || attachment.path || attachment.url || "";
  if (!filePath) return null;
  if (filePath.startsWith("http://") || filePath.startsWith("https://"))
    return filePath;
  const baseUrl =
    process.env.REACT_APP_API_URL?.replace(/\/+$/, "") ||
    "http://localhost:8000";
  const cleanPath = filePath.replace(/^\/+/, "");
  return `${baseUrl}/storage/${cleanPath}`;
};

const formatTimeAgo = (dateStr) => {
  const now = new Date();
  const commentDate = new Date(dateStr);
  const diffSec = Math.floor((now - commentDate) / 1000);
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

const handlePasteImage = (event, taskId, setCommentFiles, toast) => {
  const items = event.clipboardData?.items;
  if (!items) return;
  const imageFiles = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf("image") !== -1) {
      const file = items[i].getAsFile();
      if (file) imageFiles.push(file);
    }
  }
  if (imageFiles.length > 0) {
    event.preventDefault();
    setCommentFiles((prev) => {
      const existingFiles = prev[taskId] || [];
      const existingArray =
        existingFiles instanceof FileList
          ? Array.from(existingFiles)
          : existingFiles;
      return { ...prev, [taskId]: [...existingArray, ...imageFiles] };
    });
    toast?.success(`📸 ${imageFiles.length} image(s) pasted`);
  }
};

// ============ IMAGE PREVIEW COMPONENT ============
const ImagePreview = ({ file, onRemove, index }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (file && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);
  if (!file) return null;
  return (
    <Box
      sx={{
        position: "relative",
        width: 100,
        height: 100,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        "&:hover .remove-btn": { opacity: 1 },
      }}
    >
      {file.type.startsWith("image/") ? (
        <img
          src={previewUrl || URL.createObjectURL(file)}
          alt={file.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f5f5f5",
          }}
        >
          <AttachFileIcon color="action" />
        </Box>
      )}
      {onRemove && (
        <IconButton
          className="remove-btn"
          size="small"
          onClick={() => onRemove(index)}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            bgcolor: "rgba(255, 255, 255, 0.9)",
            width: 24,
            height: 24,
            opacity: 0,
            transition: "opacity 0.2s",
            "&:hover": { bgcolor: "rgba(255, 0, 0, 0.8)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

// ============ AUTHENTICATED IMAGE COMPONENT ============
const AuthenticatedImage = ({ attachment, style, onError }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    const fetchImage = async () => {
      if (!attachment?.id) return;
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/attachments/${attachment.id}/download`,
          {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        setImageUrl(url);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load image:", err);
        setError(true);
        setLoading(false);
        onError?.(err);
      }
    };
    fetchImage();
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [attachment?.id]);
  if (loading)
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  if (error || !imageUrl)
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <AttachFileIcon color="action" />
      </Box>
    );
  return (
    <img
      src={imageUrl}
      alt={attachment.original_name}
      style={style}
      onError={(e) => {
        e.target.style.display = "none";
        e.target.nextSibling.style.display = "flex";
      }}
    />
  );
};

// ============ IMAGE PREVIEW DIALOG ============
const ImagePreviewDialog = ({
  open,
  onClose,
  imageUrl,
  imageName,
  onDownload,
}) => {
  const [imageError, setImageError] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          bgcolor: "#000",
          maxWidth: "100%!important",
          p: 3,
        },
      }}
    >
      <DialogContent
        fullWidth
        sx={{
          p: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
          maxWidth: "100%!important",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "100%!important" }}>
          {imageError ? (
            <Box sx={{ color: "#fff", textAlign: "center" }}>
              <AttachFileIcon sx={{ fontSize: 80, mb: 2 }} />
              <Typography>Image failed to load</Typography>
            </Box>
          ) : (
            <img
              src={imageUrl}
              alt={imageName}
              style={{
                width: "100%",
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
              onError={() => setImageError(true)}
            />
          )}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                zIndex: 10,
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={onDownload}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                zIndex: 10,
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ============ ATTACHMENTS LIST DIALOG ============
const AttachmentsListDialog = ({
  open,
  onClose,
  attachments,
  tasks,
  taskComments,
  onDownload,
  theme,
}) => {
  const getTaskName = (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    return task?.title || "Unknown Task";
  };
  const allAttachments = useMemo(() => {
    const aggregated = [];
    tasks.forEach((task) => {
      if (task.attachments && Array.isArray(task.attachments)) {
        task.attachments.forEach((att) => {
          aggregated.push({
            ...att,
            source_type: "task",
            task_id: task.id,
            task_title: task.title,
          });
        });
      }
    });
    Object.values(taskComments).forEach((comments) => {
      if (Array.isArray(comments)) {
        comments.forEach((comment) => {
          if (comment.attachments && Array.isArray(comment.attachments)) {
            comment.attachments.forEach((att) => {
              aggregated.push({
                ...att,
                source_type: "comment",
                task_id: comment.task_id,
                task_title: getTaskName(comment.task_id),
                comment_id: comment.id,
              });
            });
          }
        });
      }
    });
    return aggregated;
  }, [tasks, taskComments]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Project Attachments ({allAttachments.length})
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {allAttachments.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <AttachFileIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
            <Typography>No attachments found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Attachment</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allAttachments.map((att) => (
                  <TableRow key={`${att.source_type}-${att.id}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {att.task_title || "Unknown Task"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {att.original_name?.match(
                          /\.(jpg|jpeg|png|gif|webp)$/i
                        ) ? (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              overflow: "hidden",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <AuthenticatedImage
                              attachment={att}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        ) : (
                          <AttachFileIcon color="action" />
                        )}
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 200 }}
                        >
                          {att.original_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          att.source_type === "comment" ? "Comment" : "Task"
                        }
                        size="small"
                        color={
                          att.source_type === "comment" ? "secondary" : "primary"
                        }
                        sx={{ height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {att.size
                          ? `${(att.size / 1024).toFixed(1)} KB`
                          : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => onDownload(att)}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ MAIN COMPONENT ============
const ProjectBoardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser, company } = useAuth();

  // State
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [comments, setComments] = useState([]);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [taskComments, setTaskComments] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [newCommentText, setNewCommentText] = useState({});
  const [commentFiles, setCommentFiles] = useState({});
  const [isTracking, setIsTracking] = useState(false);
  const [users, setUsers] = useState([]);
  const [expandedColumn, setExpandedColumn] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [showBurndown, setShowBurndown] = useState(false);
  const [burndownData, setBurndownData] = useState(null);
  const [burndownLoading, setBurndownLoading] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({
    url: "",
    name: "",
    attachment: null,
  });

  // Project Selector & Form State
const [availableProjects, setAvailableProjects] = useState([]);
const [clients, setClients] = useState([]);
const [allUsers, setAllUsers] = useState([]);
const [projectStatuses, setProjectStatuses] = useState([]);
const [projectDialogOpen, setProjectDialogOpen] = useState(false);
const [dialogType, setDialogType] = useState("add");
const [projectFormData, setProjectFormData] = useState({
  name: "", description: "", start_date: "", end_date: "", status_id: "",
  assigned_user_ids: [], client_id: "", billing_model: "hourly",
  hourly_rate: "", fixed_price: "", currency: "USD", estimated_hours: "", default_sprint_duration_weeks: 2,
});
const [projectFormErrors, setProjectFormErrors] = useState({});
const [projectFiles, setProjectFiles] = useState([]);
const [projectAttachments, setProjectAttachments] = useState([]);
const [currentProjectForEdit, setCurrentProjectForEdit] = useState(null);
const [isProjectSubmitting, setIsProjectSubmitting] = useState(false);
const projectFileInputRef = useRef(null);

const userMap = useMemo(() => {
  const map = {};
  allUsers.forEach((u) => { map[u.id] = u; });
  return map;
}, [allUsers]);


  // Feature States
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addStatusOpen, setAddStatusOpen] = useState(false);
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [attachmentsDialogOpen, setAttachmentsDialogOpen] = useState(false);
  const [allProjectAttachments, setAllProjectAttachments] = useState([]);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    status_id: null,
    assigned_user_ids: [],
    due_date: null,
    sprint_id: "",
    story_points: "",
    isBacklog: false,
  });
  const [editTaskData, setEditTaskData] = useState(null);
  const [newStatus, setNewStatus] = useState({
    name: "",
    key: "",
    color: "#9CA3AF",
  });
  const [editStatusData, setEditStatusData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sprints, setSprints] = useState([]);
  const [taskFiles, setTaskFiles] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const fileInputRef = useRef(null);

  // Handling File Type Validation
  const ALLOWED_FILE_TYPES = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt'],
  };
  // Create accept string for file input (MIME types for HTML accept attribute)
  const ACCEPTED_MIME_TYPES = Object.keys(ALLOWED_FILE_TYPES).join(',');
  // Create extensions array for validation
  const ACCEPTED_EXTENSIONS_LIST = Object.values(ALLOWED_FILE_TYPES).flat();
  // Available colors
  const AVAILABLE_COLORS = [
    "#2196F3",
    "#4CAF50",
    "#FF9800",
    "#F44336",
    "#9C27B0",
    "#607D8B",
    "#E91E63",
    "#10B981",
    "#3B82F6",
    "#EF4444",
    "#F59E0B",
    "#8B5CF6",
    "#6B7280",
    "#EC4899",
    "#14B8A6",
    "#F97316",
    "#84CC16",
    "#A855F7",
  ];

  // Permission checks
  const hasPermission = (perm) => {
    if (!authUser) return false;
    if (authUser.hasPermission && typeof authUser.hasPermission === "function")
      return authUser.hasPermission(perm);
    const role = authUser.teamMembership?.role;
    if (!role) return false;
    if (role.name?.toLowerCase() === "system administrator") return true;
    const perms = Array.isArray(role.permissions)
      ? role.permissions.map((p) => p.name)
      : [];
    return perms.includes(perm);
  };
  const canViewBoard = hasPermission("project.board.view");
  const canManageBoard = hasPermission("project.board.manage");
  const canUpdateTask = hasPermission("task.update");
  const canManageTaskTeam = hasPermission("task.manage.team");
  const canCreateComment = hasPermission("task.comment.add");
  const canDeleteComment = hasPermission("task.comment.delete");
  const canDeleteTask = hasPermission("task.delete");
  const isAdmin =
    hasPermission("project.admin") ||
    hasPermission("project.manage") ||
    authUser?.role?.name?.toLowerCase() === "system administrator";

  // ============ BACKLOG STATUS MANAGEMENT ============
  // Get backlog status from backend (order = 1) or create virtual backlog with order 0
  const backlogStatusId = useMemo(() => {
    const backlog = taskStatuses.find(
      (s) => s.key === "backlog" || s.name?.toLowerCase() === "backlog"
    );
    return backlog?.id || null;
  }, [taskStatuses]);

  // Columns with Backlog ALWAYS first (order 0 for UI, DB order starts from 1)
  const columns = useMemo(() => {
    // Sort statuses by their order from backend
    const sortedStatuses = [...taskStatuses].sort((a, b) => {
      const aOrder = a.order ?? 999;
      const bOrder = b.order ?? 999;
      return aOrder - bOrder;
    });

    const baseColumns = sortedStatuses.map((status) => ({
      id: (status.key || status.name?.toLowerCase().replace(/\s+/g, '_')).toLowerCase(),
      title: status.name,
      color: status.color || "#9CA3AF",
      statusId: status.id,
      isBacklog: status.key === "backlog" || status.name?.toLowerCase() === "backlog",
      order: status.order || 0,
    }));

    // Ensure Backlog column is always first (leftmost position)
    const existingBacklogIndex = baseColumns.findIndex((c) => c.isBacklog);
    if (existingBacklogIndex !== -1 && existingBacklogIndex !== 0) {
      // Move backlog to first position if it's not already there
      const [backlog] = baseColumns.splice(existingBacklogIndex, 1);
      baseColumns.unshift(backlog);
    }

    // If no backlog status exists in DB, create virtual backlog column (order 0)
    const hasBacklog = baseColumns.some((c) => c.isBacklog);
    if (!hasBacklog) {
      baseColumns.unshift({
        id: "backlog",
        title: "Backlog",
        color: "#607D8B",
        statusId: null,
        isBacklog: true,
        order: 0,
      });
    }

    return baseColumns;
  }, [taskStatuses]);

  const projectAssignedUserIds = useMemo(
    () => new Set((project?.assigned_users || []).map((u) => u.id)),
    [project?.assigned_users]
  );
  const projectUsers = useMemo(
    () => users.filter((user) => projectAssignedUserIds.has(user.id)),
    [users, projectAssignedUserIds]
  );
  // Filter projects based on permission checks
  const filteredProjects = useMemo(() => {
    return availableProjects.filter((project) => {
      if (!hasPermission("project.board.view")) return false;

      return true;
    });
  }, [availableProjects, hasPermission]);

  // Check if task is overdue
  const isTaskOverdue = useCallback((task) => {
    if (!task.due_date) return false;
    const dueDate = new Date(task.due_date);
    const now = new Date();
    const isCompleted = ["completed", "done", "closed"].includes(
      task.statusObj?.key?.toLowerCase()
    );
    return now > dueDate && !isCompleted;
  }, []);

  // Get effective tasks - move overdue OR null status tasks to backlog
  const getEffectiveTasks = useCallback(() => {
    return tasks.map((task) => {
      const isOverdue = isTaskOverdue(task);
      // ✅ TRIPLE FALLBACK for statusObj (API → Global → Find by ID)
      const statusFromApi = task.status;
      const statusFromTaskObj = task.statusObj;
      const statusFromGlobal = taskStatuses.find(s => String(s.id) === String(task.status_id));
      const statusObj = task.statusObj || task.status || taskStatuses.find(s => String(s.id) === String(task.status_id)) || null;

      // ✅ SAFE key extraction with MULTIPLE fallbacks
      let statusKey = statusObj?.key?.toLowerCase();
      if (!statusKey && statusObj?.name) {
        // Fallback: generate key from name if key is missing
        statusKey = statusObj.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      }
      if (!statusKey) statusKey = "backlog"; // Ultimate fallback

      const hasNoStatus = !task.status_id;
      const isCompleted = ["completed", "done", "closed"].includes(statusKey);

      // ✅ Move to backlog if: overdue OR no status OR already backlog
      if (
        (isOverdue && task.status_id !== backlogStatusId && !isCompleted) ||
        hasNoStatus ||
        task.status_id === backlogStatusId
      ) {
        return {
          ...task,
          statusObj,
          _isOverdueMoved: isOverdue,
          effectiveStatusKey: "backlog",
        };
      }
      return {
        ...task,
        statusObj,
        effectiveStatusKey: statusKey,
      };
    });
  }, [tasks, isTaskOverdue, backlogStatusId, taskStatuses]);

  // ============ BURNDOWN ============
  const fetchBurndownData = async () => {
    if (!project?.id || !isAdmin) return;
    setBurndownLoading(true);
    try {
      const res = await projectAPI.getBurndown(project.id);
      const data = res.data?.data || res.data;
      setBurndownData(data);
    } catch (err) {
      console.error("Failed to load burndown:", err);
      console.error("Error response:", err.response);
      setBurndownData(null);
    } finally {
      setBurndownLoading(false);
    }
  };

  useEffect(() => {
    const loadProjectFormData = async () => {
      try {
        // 1. Fetch available projects for the user
        const projectsRes = await projectAPI.getAll(1);
        const projectsData = projectsRes.data.data || projectsRes.data;
        const enrichedProjects = projectsData.map(p => ({
          ...p,
          assignedUsers: (p.assigned_users || []).map(u => ({ ...u, role: u.role?.name || "Member" }))
        }));
        setAvailableProjects(enrichedProjects);

        // 2. Fetch Clients, Users, Statuses
        const [clientsRes, usersRes, statusesRes] = await Promise.all([
          clientAPI.getAll(),
          userAPI.getAll(),
          axios.get(`${process.env.REACT_APP_API_URL}/statuses?type=project`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
          })
        ]);

        setClients(clientsRes.data || []);
        
        // ✅ Format users properly like ProjectPage.js does
        const activeMembers = usersRes.data.filter((member) => member.is_active);
        const formattedUsers = activeMembers.map((member) => ({
          id: member.user?.id || member.id,
          name: member.user?.name || member.name,
          email: member.user?.email || member.email,
          role: member.role?.name || member.user?.role?.name || "N/A",
          avatar: member.user?.avatar || member.avatar,
        }));
        
        setAllUsers(formattedUsers);
        setProjectStatuses(statusesRes.data || []);
      } catch (err) {
        console.error("Failed to load project form data:", err);
      }
    };
    loadProjectFormData();
  }, []);

  useEffect(() => {
    if (project?.id && isAdmin) {
      fetchBurndownData();
    }
  }, [project?.id, isAdmin]);

  const fetchSprints = async (projectId) => {
    if (!projectId) {
      setSprints([]);
      return;
    }
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/${projectId}/sprints`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSprints(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load sprints:", err);
      setSprints([]);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await teamMemberAPI.getAll();
        let members = [];
        if (Array.isArray(response.data)) members = response.data;
        else if (response.data?.data && Array.isArray(response.data.data))
          members = response.data.data;
        else if (response.data?.members && Array.isArray(response.data.members))
          members = response.data.members;
        else if (response.data?.users && Array.isArray(response.data.users))
          members = response.data.users;
        const formattedUsers = members.map((member) => {
          const userData = member.user || member;
          return {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            avatar: userData.avatar,
            role: member.role?.name || userData.role?.name || "N/A",
          };
        });
        setUsers(formattedUsers);
      } catch (err) {
        console.error("❌ Failed to load users:", err);
        toast.error("Failed to load team members");
      }
    };
    fetchUsers();
  }, []);

  // Fetch task statuses from backend only (no frontend creation)
  useEffect(() => {
    const fetchTaskStatuses = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/statuses?type=task&project_id=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        let statuses = res.data || [];
        // Store the order from backend for column ordering
        const ordered = statuses
          .filter((s) => s.order !== undefined)
          .map((s) => ({ id: s.id, order: s.order }));
        if (ordered.length > 0) {
          setColumnOrder(ordered);
        }
        setTaskStatuses(statuses);
      } catch (err) {
        console.error("Failed to load task statuses:", err);
        setError("Failed to load task statuses");
      }
    };
    fetchTaskStatuses();
  }, [id]);

  const fetchAllProjectAttachments = async () => {
    if (!project?.id) return;
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/attachments?project_id=${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllProjectAttachments(res.data || []);
    } catch (err) {
      console.error("Failed to load project attachments:", err);
    }
  };

  useEffect(() => {
  const loadData = async () => {
    // ✅ ADD THIS CHECK - if no id, don't try to load
    if (!id) {
      setLoading(false);
      return;
    }
    
    if (!canViewBoard) {
      setError("Access Denied");
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const projectResponse = await projectAPI.getById(id);
      const projectData = projectResponse.data?.data || projectResponse.data;
      
      const normalizedAssignedUsers = (projectData.assigned_users || []).map(
        (member) => {
          const userData = member.user || member;
          return {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            avatar: userData.avatar,
            role: member.role?.name || userData.role?.name || "N/A",
          };
        }
      );
      
      setProject({ ...projectData, assigned_users: normalizedAssignedUsers });
      fetchSprints(id);
      
      const token = localStorage.getItem('access_token');
      const tasksResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/${id}/tasks-all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const allTasks = tasksResponse.data?.data || tasksResponse.data || [];
      const enrichedTasks = allTasks.map((t) => ({
        ...t,
        statusObj: t.status || taskStatuses.find(s => String(s.id) === String(t.status_id)) || null,
        assignedUsers: t.assigned_users || t.assignedUsers || [],
        attachments: t.attachments || [],
        comment_count: t.comment_count || 0,
        tracked_time: t.tracked_time || { formatted: "00h 00m", is_running: false },
      }));
      
      setTasks(enrichedTasks);
      fetchAllProjectAttachments();
    } catch (err) {
      console.error("Failed to load project or tasks:", err);
      setError("Failed to load project or tasks");
    } finally {
      setLoading(false);
    }
  };
  
  loadData();
}, [id, canViewBoard, taskStatuses]); // ✅ Keep these dependencies

  // ============ REALTIME ============
  const handleTaskUpdates = useCallback(
    (updatedTasks) => {
      if (!Array.isArray(updatedTasks) || updatedTasks.length === 0) return;
      setTasks((prev) => {
        const existingMap = new Map(prev.map((p) => [Number(p.id), p]));
        const merged = [];
        updatedTasks.forEach((upd) => {
          const taskId = Number(upd.id);
          if (upd.is_deleted === true || upd.deleted === true) {
            if (existingMap.has(taskId)) existingMap.delete(taskId);
            return;
          }
          const isAssigned = (
            upd.assignedUsers ||
            upd.assigned_users ||
            []
          ).some((u) => Number(u.id) === Number(authUser?.id));
          const isCreator = Number(upd.created_by) === Number(authUser?.id);
          if (!isAssigned && !isCreator && !hasPermission("task.view.all"))
            return;
          const enriched = {
            ...upd,
            assignedUsers: (upd.assignedUsers || upd.assigned_users || []).map(
              (u) => ({ ...u, role: u.role?.name || "No Role" })
            ),
            attachments: upd.attachments || [],
            statusObj: upd.status || null,
            status_id: upd.status_id,
            comment_count: upd.comment_count || 0,
            tracked_time: upd.tracked_time || {
              formatted: "00h 00m",
              is_running: false,
            },
          };
          if (existingMap.has(taskId)) {
            merged.push({ ...existingMap.get(taskId), ...enriched });
            existingMap.delete(taskId);
          } else {
            merged.push(enriched);
          }
          if (taskId === expandedTaskId) {
            clearTimeout(window.__reloadTimer);
            window.__reloadTimer = setTimeout(() => {
              if (typeof loadTaskComments === "function")
                loadTaskComments(taskId);
            }, 50);
          }
        });
        existingMap.forEach((p) => merged.push(p));
        return merged.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      });
    },
    [authUser?.id, expandedTaskId, hasPermission]
  );

  useEffect(() => {
    realtime.start("tasks");
    return () => {
      realtime.stop("tasks");
    };
  }, []);

  useEffect(() => {
    realtime.on("tasks", handleTaskUpdates);
    return () => {
      realtime.off("tasks", handleTaskUpdates);
    };
  }, [handleTaskUpdates]);

  useEffect(() => {
    if (!expandedTaskId) return;
    const key = `comments-task-${expandedTaskId}`;
    const handleNewComments = (incoming) => {
      if (!incoming || incoming.length === 0) return;
      const deletes = incoming.filter((c) => c.is_deleted);
      const newOnes = incoming.filter((c) => !c.is_deleted);
      if (deletes.length > 0) {
        setTaskComments((prev) => {
          const updated = { ...prev };
          deletes.forEach((d) => {
            Object.keys(updated).forEach((tid) => {
              updated[tid] = (updated[tid] || []).filter(
                (c) => c.id !== d.id
              );
            });
          });
          return updated;
        });
      }
      if (newOnes.length > 0) {
        setTaskComments((prev) => {
          const updated = { ...prev };
          newOnes.forEach((c) => {
            const tid = c.task_id;
            const list = updated[tid] || [];
            const exists = list.some((x) => x.id === c.id);
            if (!exists)
              updated[tid] = [c, ...list].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
              );
          });
          return updated;
        });
      }
    };
    realtime.on(key, handleNewComments);
    realtime.start("comments", { task_id: expandedTaskId });
    return () => {
      realtime.off(key, handleNewComments);
      realtime.stop(key);
    };
  }, [expandedTaskId, authUser]);

  // ============ ATTACHMENTS ============
  const fetchAttachments = async (taskId = null) => {
    try {
      const token = localStorage.getItem("access_token");
      const base = `${process.env.REACT_APP_API_URL}/attachments`;
      const url = taskId
        ? `${base}?task_id=${taskId}`
        : `${base}?project_id=${id}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (taskId) setAttachments(res.data);
    } catch (err) {
      console.error("Failed to load attachments:", err);
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/attachments/${attachment.id}/download`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed: Please re-login.");
    }
  };

  const handleImagePreview = async (attachment) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/attachments/${attachment.id}/download`,
        { responseType: "blob", headers: { Authorization: `Bearer ${token}` } }
      );
      const imageUrl = window.URL.createObjectURL(new Blob([response.data]));
      setSelectedImage({
        url: imageUrl,
        name: attachment.original_name,
        attachment,
      });
      setImagePreviewOpen(true);
    } catch (error) {
      console.error("Failed to load image preview:", error);
      toast.error("Failed to load image preview");
    }
  };

  const handlePreviewDownload = async () => {
    if (selectedImage.attachment)
      await handleDownload(selectedImage.attachment);
  };

  // ============ DRAG AND DROP ============
  const onDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    ) {
      return;
    }
    // Handle COLUMN reordering
    if (type === "column") {
      // FIX: Prevent dragging Backlog column (order 0, always first)
      if (columns[source.index]?.isBacklog) {
        toast.error("Backlog column cannot be moved - it must stay at the leftmost position");
        return;
      }
      if (!canManageBoard) {
        toast.error("No permission to reorder columns");
        return;
      }
      // FIX: Get only reorderable columns (exclude backlog)
      const reorderableColumns = columns.filter((col) => !col.isBacklog);
      // FIX: Adjust indices because source/destination include backlog in the full list
      // But we're working with reorderableColumns which excludes backlog
      let adjustedSourceIndex = source.index;
      let adjustedDestinationIndex = destination.index;
      // If backlog is at position 0, we need to adjust indices
      const hasBacklogAtStart = columns[0]?.isBacklog;
      if (hasBacklogAtStart) {
        // Subtract 1 from indices since backlog takes position 0
        adjustedSourceIndex = source.index - 1;
        adjustedDestinationIndex = destination.index - 1;
      }
      // Validate adjusted indices
      if (
        adjustedSourceIndex < 0 ||
        adjustedSourceIndex >= reorderableColumns.length ||
        adjustedDestinationIndex < 0 ||
        adjustedDestinationIndex > reorderableColumns.length
      ) {
        console.error("Invalid column indices after adjustment");
        toast.error("Failed to reorder columns");
        return;
      }
      const newColumns = Array.from(reorderableColumns);
      const [removed] = newColumns.splice(adjustedSourceIndex, 1);
      newColumns.splice(adjustedDestinationIndex, 0, removed);
      try {
        // FIX: Order starts from 2 since backlog is order 1 (DB), UI shows order 0
        const statusOrder = newColumns.map((col, index) => ({
          id: col.statusId,
          order: index + 2, // Backlog is 1, others start from 2
        }));
        await statusAPI.reorder({ statuses: statusOrder });
        setColumnOrder(statusOrder);
        // Update taskStatuses with new order
        setTaskStatuses((prev) =>
          prev.map((status) => {
            const newOrder = statusOrder.find((o) => o.id === status.id);
            if (newOrder) {
              return { ...status, order: newOrder.order };
            }
            return status;
          })
        );
        toast.success("Column order updated");
      } catch (err) {
        console.error("Failed to save column order:", err);
        try {
          const token = localStorage.getItem("access_token");
          for (const col of newColumns) {
            await axios.put(
              `${process.env.REACT_APP_API_URL}/statuses/${col.statusId}`,
              { order: newColumns.indexOf(col) + 2 },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          }
          const statusOrder = newColumns.map((col, index) => ({
            id: col.statusId,
            order: index + 2,
          }));
          setColumnOrder(statusOrder);
          setTaskStatuses((prev) =>
            prev.map((status) => {
              const newOrder = statusOrder.find((o) => o.id === status.id);
              if (newOrder) {
                return { ...status, order: newOrder.order };
              }
              return status;
            })
          );
          toast.success("Column order updated");
        } catch (fallbackErr) {
          console.error("Fallback reorder failed:", fallbackErr);
          toast.error("Failed to save column order. Please refresh the page.");
        }
      }
      return;
    }
    // Handle TASK dragging
    if (!canManageBoard) {
      toast.error("You do not have permission to manage the board.");
      return;
    }
    const taskId = draggableId;
    const newStatusKey = destination.droppableId;
    // Prevent moving to backlog via drag (only automatic for overdue)
    if (newStatusKey === "backlog") {
      toast.error("Tasks can only be moved to Backlog automatically when overdue");
      return;
    }
    const newStatus = taskStatuses.find((s) => s.key === newStatusKey);
    if (!newStatus) {
      setError("Invalid status selected");
      return;
    }
    await updateTaskStatus(taskId, newStatus.id);
  };

  const updateTaskStatus = async (taskId, statusId) => {
    // Permission check
    if (!canManageBoard && !canUpdateTask) {
      toast.error("You do not have permission to change task status");
      return;
    }
    const updatedTasks = [...tasks];
    const taskIndex = updatedTasks.findIndex((t) => t.id === parseInt(taskId));
    if (taskIndex !== -1) {
      const newStatusObj = taskStatuses.find((s) => s.id === statusId);
      updatedTasks[taskIndex] = {
        ...updatedTasks[taskIndex],
        status: newStatusObj,
        status_id: statusId,
        statusObj: newStatusObj,
      };
      setTasks(updatedTasks);
    }
    setIsDragging(true);
    setError("");
    try {
      await taskAPI.update(taskId, { status_id: statusId });
      toast.success("Task status updated");
    } catch (err) {
      console.error("Failed to update task status:", err);
      setError("Failed to update task status. Reverting changes...");
      toast.error("Failed to update task status");
      const tasksResponse = await taskAPI.getAll();
      const projectTasks = tasksResponse.data.filter(
        (task) => task.project_id == id
      );
      const enrichedTasks = projectTasks.map((t) => ({
        ...t,
        statusObj: t.status || null,
        assignedUsers: t.assigned_users || [],
        attachments: t.attachments || [],
        comment_count: t.comment_count || 0,
        tracked_time: t.tracked_time || {
          formatted: "00h 00m",
          is_running: false,
        },
      }));
      setTasks(enrichedTasks);
    } finally {
      setIsDragging(false);
    }
  };

  // Check and move overdue tasks AND tasks with null status to backlog
  const checkAndMoveOverdueTasks = useCallback(async () => {
    if (!backlogStatusId) return;
    const tasksToMove = tasks.filter(
      (task) =>
        // Move tasks with null/undefined status
        (!task.status_id || !task.statusObj) ||
        // Move overdue tasks (not completed)
        (isTaskOverdue(task) &&
          task.status_id !== backlogStatusId &&
          !["completed", "done", "closed"].includes(
            task.statusObj?.key?.toLowerCase()
          ))
    );
    for (const task of tasksToMove) {
      try {
        await taskAPI.update(task.id, { status_id: backlogStatusId });
        setTasks((prev) =>
          prev.map((t) =>
            t.id === task.id
              ? {
                ...t,
                status_id: backlogStatusId,
                statusObj: taskStatuses.find(
                  (s) => s.id === backlogStatusId
                ),
              }
              : t
          )
        );
        const reason = !task.status_id || !task.statusObj ? "no status" : "overdue";
        toast.info(`"${task.title}" moved to backlog (${reason})`);
      } catch (err) {
        console.error(`Failed to move task ${task.id} to backlog:`, err);
      }
    }
  }, [tasks, isTaskOverdue, backlogStatusId, taskStatuses]);

  useEffect(() => {
    if (tasks.length > 0 && backlogStatusId) {
      checkAndMoveOverdueTasks();
    }
  }, [tasks, backlogStatusId, checkAndMoveOverdueTasks]);

  // ========= Handle Projects ============ 

  const handleOpenAddProject = () => {
    setDialogType("add");
    setCurrentProjectForEdit(null);
    
    const assignedUserIds = authUser?.id ? [authUser.id] : [];
    
    setProjectFormData({
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      status_id: "",
      assigned_user_ids: [...new Set(assignedUserIds)], // Guaranteed array
      client_id: "",
      billing_model: "hourly",
      hourly_rate: "",
      fixed_price: "",
      currency: "USD",
      estimated_hours: "",
      default_sprint_duration_weeks: 2,
    });

    setProjectFiles([]);
    setProjectAttachments([]);
    setProjectFormErrors({});
    setProjectDialogOpen(true);
  };

const handleOpenEditProject = () => {
  if (!project) return;
  if (!hasPermission("project.update") && !hasPermission("project.manage.team") && !hasPermission("project.update.status")) {
    toast.error("No permission to edit this project.");
    return;
  }
  setDialogType("edit");
  setCurrentProjectForEdit(project);
  setProjectFormData({
    name: hasPermission("project.update") ? project.name : "",
    description: hasPermission("project.update") ? project.description : "",
    start_date: hasPermission("project.update") ? convertToLocalInput(project.start_date, company?.timezone) : "",
    end_date: hasPermission("project.update") ? convertToLocalInput(project.end_date, company?.timezone) : "",
    status_id: hasPermission("project.update.status") ? project.status_id : "",
    assigned_user_ids: hasPermission("project.manage.team") ? project.assigned_users?.map(u => u.id) || [] : [],
    client_id: hasPermission("project.update") ? project.client_id || "" : "",
    billing_model: hasPermission("project.update") ? project.billing_model || "hourly" : "hourly",
    hourly_rate: hasPermission("project.update") ? project.hourly_rate || "" : "",
    fixed_price: hasPermission("project.update") ? project.fixed_price || "" : "",
    currency: hasPermission("project.update") ? project.currency || "USD" : "USD",
    estimated_hours: hasPermission("project.update") ? project.estimated_hours || "" : "",
    default_sprint_duration_weeks: hasPermission("project.update") ? (project.default_sprint_duration_weeks || 2) : 2,
  });
  setProjectFiles([]);
  setProjectAttachments(project.attachments || []);
  setProjectFormErrors({});
  setProjectDialogOpen(true);
};

const handleProjectFormChange = (e) => {
  const { name, value } = e.target;
  setProjectFormData(prev => ({ ...prev, [name]: value }));
  setProjectFormErrors(prev => ({ ...prev, [name]: null }));
};

const handleProjectUserChange = (e) => {
  // ✅ EXACT MATCH with ProjectPage.js
  const newSelection = e.target.value;
  const updatedSelection = [...new Set(newSelection)];
  
  if (!isAdmin) {
    const creatorId = currentProjectForEdit?.created_by;
    const currentUserId = authUser?.id;
    if (creatorId && !updatedSelection.includes(creatorId)) {
      updatedSelection.push(creatorId);
    }
    if (currentUserId && !updatedSelection.includes(currentUserId)) {
      updatedSelection.push(currentUserId);
    }
  }
  setProjectFormData((prev) => ({ ...prev, assigned_user_ids: updatedSelection }));
};

const handleProjectFileChange = (e) => {
  const files = Array.from(e.target.files || []);
  const valid = [], invalid = [];
  files.forEach(f => {
    const v = validateFile(f);
    v.valid ? valid.push(f) : invalid.push({ name: f.name, reason: v.reason });
  });
  if (invalid.length) invalid.forEach(f => toast.error(`❌ ${f.name}: ${f.reason}`));
  setProjectFiles(valid);
  e.target.value = "";
};

const handleProjectDialogClose = () => {
  setProjectDialogOpen(false);
  setCurrentProjectForEdit(null);
  setProjectFiles([]);
  setProjectAttachments([]);
  setProjectFormErrors({});
  setProjectFormData({
    name: "", description: "", start_date: "", end_date: "", status_id: "",
    assigned_user_ids: [], client_id: "", billing_model: "hourly",
    hourly_rate: "", fixed_price: "", currency: "USD", estimated_hours: "", default_sprint_duration_weeks: 2,
  });
  if (projectFileInputRef.current) projectFileInputRef.current.value = "";
};

const handleProjectSubmit = async (e) => {
  e.preventDefault();
  setIsProjectSubmitting(true);
  try {
    const fd = new FormData();
    const canUpdateGeneral = hasPermission("project.update");
    const canUpdateStatus = hasPermission("project.update.status");
    const canManageTeam = hasPermission("project.manage.team");

    if (canUpdateGeneral) {
      Object.entries(projectFormData).forEach(([key, value]) => {
        if (key === "assigned_user_ids" && canManageTeam) {
          value.forEach(v => fd.append(`${key}[]`, v));
        } else if (key === "status_id" && canUpdateStatus) {
          fd.append(key, value);
        } else if (value !== undefined && value !== null && key !== "assigned_user_ids") {
          if (Array.isArray(value)) value.forEach(v => fd.append(`${key}[]`, v));
          else fd.append(key, value);
        }
      });
    } else {
      if (canUpdateStatus && projectFormData.status_id) fd.append("status_id", projectFormData.status_id);
      if (canManageTeam && projectFormData.assigned_user_ids.length > 0) {
        projectFormData.assigned_user_ids.forEach(v => fd.append("assigned_user_ids[]", v));
      }
    }
    projectFiles.forEach(f => fd.append("attachments[]", f));

    let response;
    let baseData;
    if (dialogType === "add") {
      response = await projectAPI.create(fd);
      baseData = response.data;
    } else {
      response = await projectAPI.update(currentProjectForEdit.id, fd);
      baseData = response.data.project || response.data;
    }

    // Update local projects list
    const enriched = { ...baseData, assignedUsers: (baseData.assigned_users || []).map(u => ({ ...u, role: u.role?.name || "Member" })) };
    setAvailableProjects(prev => dialogType === "add" ? [enriched, ...prev] : prev.map(p => p.id === baseData.id ? enriched : p));

    // Navigate to the new/edited project's board
    navigate(`/projects/${baseData.id}/board`, { replace: true });
    toast.success(`Project ${dialogType === "add" ? "created" : "updated"} successfully`);
    handleProjectDialogClose();
  } catch (err) {
    const res = err.response?.data;
    if (err.response?.status === 422 && res?.errors) setProjectFormErrors(res.errors);
    else toast.error(res?.error || res?.message || "Operation failed");
  } finally {
    setIsProjectSubmitting(false);
  }
};

// Handle project switch via dropdown
const handleProjectSelect = (e) => {
  const selectedId = e.target.value;
  if (selectedId) navigate(`/projects/${selectedId}/board`);
};

  // ============ TASK DETAILS & COMMENTS ============
  const handleViewDetails = (task) => {
    setViewingTask(task);
    fetchAttachments(task.id);
    setExpandedTaskId(task.id);
    setViewDetailsOpen(true);
    checkActiveTimer(task.id);
    loadTaskComments(task.id);
  };

  const handleCloseDetails = () => {
    setViewDetailsOpen(false);
    setViewingTask(null);
    setAttachments([]);
    setComments([]);
    setExpandedTaskId(null);
    setIsTracking(false);
  };

  const loadTaskComments = async (taskId) => {
    setCommentLoading((prev) => ({ ...prev, [taskId]: true }));
    try {
      const res = await commentAPI.getByTask(taskId);
      setTaskComments((prev) => ({ ...prev, [taskId]: res.data }));
    } catch (err) {
      console.error("Failed to load task comments:", err);
    } finally {
      setCommentLoading((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const handlePostComment = async (taskId) => {
    let content = (newCommentText[taskId] || "").trim();
    if (!content) return;
    content = content.replace(/@\[([^\]]+)\]\([^)]+\)/g, "@$1");
    const fd = new FormData();
    fd.append("content", content);
    const files = commentFiles[taskId];
    if (files) Array.from(files).forEach((file) => fd.append("files[]", file));
    const tempId = `temp-${Date.now()}`;
    const tempComment = {
      id: tempId,
      content,
      user_id: authUser?.id,
      user: authUser,
      attachments: [],
      created_at: new Date().toISOString(),
      is_local: true,
    };
    setTaskComments((prev) => ({
      ...prev,
      [taskId]: [tempComment, ...(prev[taskId] || [])],
    }));
    setNewCommentText((prev) => ({ ...prev, [taskId]: "" }));
    setCommentFiles((prev) => ({ ...prev, [taskId]: null }));
    try {
      const res = await commentAPI.create(taskId, fd);
      const newComment = res.data;
      setTaskComments((prev) => {
        const list = prev[taskId] || [];
        const updated = list.map((c) => (c.id === tempId ? newComment : c));
        return { ...prev, [taskId]: updated };
      });
      setNewCommentText((prev) => ({ ...prev, [taskId]: "" }));
      setCommentFiles((prev) => ({ ...prev, [taskId]: null }));
    } catch (err) {
      console.error("Failed to post comment:", err);
      setTaskComments((prev) => ({
        ...prev,
        [taskId]: (prev[taskId] || []).filter((c) => c.id !== tempId),
      }));
      toast.error(err.response?.data?.error || "Failed to post comment");
    }
  };

  const handleDeleteComment = async (taskId, commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await commentAPI.delete(commentId);
      setTaskComments((prev) => ({
        ...prev,
        [taskId]: (prev[taskId] || []).filter((c) => c.id !== commentId),
      }));
    } catch (err) {
      console.error("Failed to delete comment:", err);
      toast.error("Failed to delete comment");
    }
  };

  const checkActiveTimer = async (taskId) => {
    try {
      const res = await timeEntryAPI.listByTask(taskId);
      const isActive = res.data.some((entry) => !entry.end_time);
      const lastEntry = res.data[res.data.length - 1];
      setIsTracking(isActive);
      if (lastEntry) {
        setViewingTask((prev) => ({
          ...prev,
          tracked_time: {
            ...prev?.tracked_time,
            end_time: lastEntry.end_time || null,
          },
        }));
      }
    } catch (err) {
      console.error("Timer check error:", err);
    }
  };

  const startTimer = async () => {
    if (!viewingTask) return;
    try {
      await timeEntryAPI.start({ task_id: viewingTask.id });
      setIsTracking(true);
    } catch (err) {
      console.error("Error starting timer:", err);
    }
  };

  const stopTimer = async () => {
    try {
      await timeEntryAPI.stop();
      setIsTracking(false);
    } catch (err) {
      console.error("Error stopping timer:", err);
    }
  };

  const calculateProjectDuration = () => {
    if (!project || !project.start_date || !project.end_date) return "N/A";
    const start = new Date(project.start_date);
    const end = new Date(project.end_date);
    const diffMs = Math.abs(end - start);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const daysPart =
      diffDays > 0 ? `${diffDays} day${diffDays !== 1 ? "s" : ""}` : "";
    const hoursPart = `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
    return `${daysPart} (${hoursPart})`;
  };

  const removeFile = (taskId, index) => {
    setCommentFiles((prev) => {
      const existing = prev[taskId] || [];
      const filtered = Array.from(existing).filter((_, i) => i !== index);
      return { ...prev, [taskId]: filtered };
    });
  };

  const handleTaskFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const invalidFiles = [];
    files.forEach((file) => {
      if (!(file instanceof File)) return;
      // Get file extension
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidExtension = ACCEPTED_EXTENSIONS_LIST.includes(extension);
      // Get file MIME type
      const mimeType = file.type.toLowerCase();
      const isValidMimeType = Object.keys(ALLOWED_FILE_TYPES).some(
        type => mimeType === type || mimeType.startsWith(type.split('/')[0] + '/')
      );
      // Validate both extension AND MIME type
      if (isValidExtension && isValidMimeType) {
        validFiles.push(file);
      } else {
        invalidFiles.push({
          name: file.name,
          reason: !isValidExtension ? 'Invalid extension' : 'Invalid file type'
        });
      }
    });
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map(f => f.name).join(', ');
      toast.error(
        `❌ Rejected ${invalidFiles.length} invalid file(s): ${invalidNames}.
Allowed: jpg, jpeg, png, gif, webp, pdf, doc, docx, xls, xlsx, txt`
      );
    }
    // Only set valid files
    setTaskFiles(validFiles);
    // Reset input to allow re-selecting same file if needed
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleCreateTask = async () => {
    const errors = {};
    if (!newTask.title.trim()) errors.title = "Title is required";
    // FIX: Prioritize the status_id selected in the form dropdown.
    // Only fallback to backlog if NO status is selected.
    const statusIdToUse = newTask.status_id || backlogStatusId;
    if (!statusIdToUse) {
      setFormErrors({ ...formErrors, status_id: "Please select a column" });
      return;
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    // FIX: Strict file validation before submission
    if (taskFiles && taskFiles.length > 0) {
      const invalidFiles = taskFiles.filter(file => {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        const mimeType = file.type.toLowerCase();
        const isValidExtension = ACCEPTED_EXTENSIONS_LIST.includes(ext);
        const isValidMimeType = Object.keys(ALLOWED_FILE_TYPES).some(
          type => mimeType === type || mimeType.startsWith(type.split('/')[0] + '/')
        );
        return !(isValidExtension && isValidMimeType);
      });
      if (invalidFiles.length > 0) {
        const invalidNames = invalidFiles.map(f => f.name).join(', ');
        toast.error(`❌ Please remove invalid files: ${invalidNames}`);
        return;
      }
    }
    setIsSubmitting(true);
    setFormErrors({});
    try {
      const fd = new FormData();
      fd.append("title", newTask.title);
      fd.append("description", newTask.description || "");
      fd.append("project_id", id);
      fd.append("priority", newTask.priority || "medium");
      fd.append("status_id", statusIdToUse);
      // FIX: Safe Timezone Handling for Due Date
      const timezone = company?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (newTask.due_date) {
        const convertedDate = convertLocalToUTCStrict(newTask.due_date, timezone);
        if (convertedDate) {
          fd.append("due_date", convertedDate);
        }
      }
      if (newTask.sprint_id) fd.append("sprint_id", newTask.sprint_id);
      if (newTask.story_points) fd.append("story_points", newTask.story_points);
      fd.append("created_by", authUser?.id);
      const assignees = Array.isArray(newTask.assigned_user_ids)
        ? newTask.assigned_user_ids
        : [];
      assignees.forEach((userId) => fd.append("assigned_user_ids[]", userId));
      if (taskFiles && taskFiles.length > 0) {
        taskFiles.forEach((file) => {
          if (file instanceof File) {
            fd.append("attachments[]", file);
          }
        });
      }
      const response = await taskAPI.create(fd);
      const newTaskData = response.data;
      const createdStatus = taskStatuses.find((s) => s.id === newTaskData.status_id);
      const enrichedTask = {
        ...newTaskData,
        statusObj: createdStatus || {
          id: newTaskData.status_id,
          key: "backlog",
          name: "Unknown",
          color: "#999"
        },
        assignedUsers: users.filter((u) => assignees.includes(u.id)),
        attachments: newTaskData.attachments || [],
        comment_count: 0,
        tracked_time: { formatted: "00h 00m", is_running: false },
      };
      setTasks((prev) => [...prev, enrichedTask]);
      setAddTaskOpen(false);
      setTaskFiles([]);
      // FIX: Reset state completely
      setNewTask({
        title: "",
        description: "",
        priority: "medium",
        status_id: null,
        assigned_user_ids: [],
        due_date: null,
        sprint_id: "",
        story_points: "",
        isBacklog: false,
      });
      toast.success("✅ Task created successfully!");
      if (showBurndown && isAdmin) fetchBurndownData();
    } catch (err) {
      console.error("Failed to create task:", err);
      if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat().join(", ");
        setFormErrors({ submit: errorMessages });
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        setFormErrors({ submit: err.response?.data?.message || "Failed to create task" });
        toast.error("❌ Failed to create task");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditTask = (task) => {
    setEditTaskData(task);
    setEditTaskOpen(true);
    fetchSprints(task.project_id);
    setTaskFiles([]);
    fetchAttachments(task.id);
  };

  const handleUpdateTask = async () => {
    if (!editTaskData) return;
    // FIX: Add same file validation as create
    if (taskFiles && taskFiles.length > 0) {
      const invalidFiles = taskFiles.filter(file => {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        const mimeType = file.type.toLowerCase();
        const isValidExtension = ACCEPTED_EXTENSIONS_LIST.includes(ext);
        const isValidMimeType = Object.keys(ALLOWED_FILE_TYPES).some(
          type => mimeType === type || mimeType.startsWith(type.split('/')[0] + '/')
        );
        return !(isValidExtension && isValidMimeType);
      });
      if (invalidFiles.length > 0) {
        toast.error(`❌ Please remove invalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", editTaskData.title);
      fd.append("description", editTaskData.description || "");
      fd.append("project_id", id);
      fd.append("priority", editTaskData.priority || "medium");
      if (editTaskData.status_id) fd.append("status_id", editTaskData.status_id);
      if (editTaskData.due_date) {
        fd.append("due_date", convertLocalToUTCStrict(editTaskData.due_date, company?.timezone));
      }
      if (editTaskData.sprint_id) fd.append("sprint_id", editTaskData.sprint_id);
      if (editTaskData.story_points !== undefined && editTaskData.story_points !== null) {
        fd.append("story_points", editTaskData.story_points);
      }
      (editTaskData.assigned_user_ids || []).forEach((userId) =>
        fd.append("assigned_user_ids[]", userId)
      );
      // FIX: Only append validated files
      if (taskFiles && taskFiles.length > 0) {
        taskFiles.forEach((file) => {
          if (file instanceof File) {
            fd.append("attachments[]", file);
          }
        });
      }
      await taskAPI.update(editTaskData.id, fd);
      setTasks((prev) =>
        prev.map((t) => (t.id === editTaskData.id ? { ...t, ...editTaskData } : t))
      );
      setEditTaskOpen(false);
      setTaskFiles([]);
      toast.success("✅ Task updated successfully!");
      realtime.advanceLastCheck("tasks");
    } catch (err) {
      console.error("Failed to update task:", err);
      toast.error(err.response?.data?.message || "Failed to update task");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!canDeleteTask) {
      toast.error("No permission to delete tasks");
      return;
    }
    if (!window.confirm("Delete this task permanently?")) return;
    try {
      await taskAPI.delete(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Task deleted");
      realtime.advanceLastCheck("tasks");
    } catch (err) {
      console.error("Failed to delete task:", err);
      toast.error("Failed to delete task");
    }
  };

  // ============ CREATE/EDIT STATUS (COLUMN) ============
  const handleCreateStatus = async () => {
    if (!newStatus.name.trim()) {
      toast.error("Status name is required");
      return;
    }
    // Prevent creating backlog status (already exists)
    if (newStatus.name.toLowerCase() === "backlog") {
      toast.error("Backlog status already exists and cannot be recreated");
      return;
    }
    setIsSubmitting(true);
    try {
      // FIX: Get max order from existing statuses and add 1
      const maxOrder =
        taskStatuses.length > 0
          ? Math.max(...taskStatuses.map((s) => s.order || 0))
          : 1;
      const nextOrder = maxOrder + 1;
      const generatedKey =
        newStatus.key ||
        newStatus.name
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");
      const payload = {
        name: newStatus.name.trim(),
        key: generatedKey,
        color: newStatus.color || "#9CA3AF",
        type: "task",
        project_id: parseInt(id),
        order: nextOrder,
      };
      const response = await statusAPI.create(payload);
      const newStatusData = response.data.status || response.data;
      if (!newStatusData.id) {
        throw new Error("Status created but no ID returned");
      }
      const completeStatus = {
        id: newStatusData.id,
        name: newStatusData.name || newStatus.name.trim(),
        key: newStatusData.key || generatedKey,
        color: newStatusData.color || newStatus.color || "#9CA3AF",
        order:
          newStatusData.order !== undefined ? newStatusData.order : nextOrder,
        type: newStatusData.type || "task",
        project_id: newStatusData.project_id || parseInt(id),
        created_at: newStatusData.created_at || new Date().toISOString(),
      };
      setTaskStatuses((prev) => {
        const updated = [...prev, completeStatus];
        return updated;
      });
      const newOrderEntry = {
        id: completeStatus.id,
        order: completeStatus.order,
      };
      setColumnOrder((prev) => {
        const updated = [...prev, newOrderEntry];
        return updated;
      });
      setAddStatusOpen(false);
      setNewStatus({ name: "", key: "", color: "#9CA3AF" });
      toast.success("✅ Column added to board!");
    } catch (err) {
      console.error("❌ Failed to create status:", err);
      console.error("Error response:", err.response);
      console.error(
        "Validation errors:",
        err.response?.data?.errors || err.response?.data
      );
      const errors = err.response?.data?.errors;
      if (errors) {
        const errorMessages = Object.values(errors).flat().join(", ");
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        toast.error(err.response?.data?.message || "Failed to add column");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditStatus = (status) => {
    // Prevent editing backlog status
    if (status.key === "backlog" || status.name?.toLowerCase() === "backlog") {
      toast.error("Backlog column cannot be edited");
      return;
    }
    setEditStatusData(status);
    setNewStatus({
      name: status.name,
      key: status.key,
      color: status.color || "#9CA3AF",
    });
    setEditStatusOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!editStatusData || !newStatus.name.trim()) {
      toast.error("Status name is required");
      return;
    }
    // Prevent changing backlog status
    if (editStatusData.key === "backlog" || editStatusData.name?.toLowerCase() === "backlog") {
      toast.error("Backlog column cannot be modified");
      return;
    }
    setIsSubmitting(true);
    try {
      const generatedKey =
        newStatus.key ||
        newStatus.name
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^a-z0-9_]/g, "");
      const payload = {
        name: newStatus.name.trim(),
        key: generatedKey,
        color: newStatus.color || "#9CA3AF",
        type: "task",
        project_id: parseInt(id),
      };
      const response = await statusAPI.update(editStatusData.id, payload);
      const updatedStatusData = response.data.status || response.data;
      setTaskStatuses((prev) =>
        prev.map((s) =>
          s.id === editStatusData.id
            ? {
              ...s,
              name: updatedStatusData.name || newStatus.name.trim(),
              key: updatedStatusData.key || generatedKey,
              color: updatedStatusData.color || newStatus.color,
            }
            : s
        )
      );
      setColumnOrder((prev) => {
        const exists = prev.find((co) => co.id === editStatusData.id);
        if (exists) {
          return prev.map((co) => (co.id === editStatusData.id ? co : co));
        }
        return [
          ...prev,
          {
            id: editStatusData.id,
            order: 0,
          },
        ];
      });
      setEditStatusOpen(false);
      setEditStatusData(null);
      setNewStatus({ name: "", key: "", color: "#9CA3AF" });
      toast.success("✅ Column updated!");
    } catch (err) {
      console.error("❌ Failed to update status:", err);
      const errors = err.response?.data?.errors;
      if (errors) {
        const errorMessages = Object.values(errors).flat().join(", ");
        toast.error(`Validation failed: ${errorMessages}`);
      } else {
        toast.error(err.response?.data?.message || "Failed to update column");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStatus = async (statusId) => {
    // Prevent deleting backlog status
    const statusToDelete = taskStatuses.find(s => s.id === statusId);
    if (statusToDelete?.key === "backlog" || statusToDelete?.name?.toLowerCase() === "backlog") {
      toast.error("Backlog column cannot be deleted");
      return;
    }
    if (
      !window.confirm(
        "Delete this column? Tasks in this column will be moved to backlog."
      )
    )
      return;
    try {
      const tasksInStatus = tasks.filter((t) => t.status_id === statusId);
      if (tasksInStatus.length > 0 && backlogStatusId) {
        for (const task of tasksInStatus) {
          await taskAPI.update(task.id, { status_id: backlogStatusId });
        }
      }
      await statusAPI.delete(statusId);
      setTaskStatuses((prev) => prev.filter((s) => s.id !== statusId));
      toast.success("Column deleted. Tasks moved to backlog.");
    } catch (err) {
      console.error("Failed to delete status:", err);
      toast.error("Failed to delete column");
    }
  };

  // ============ LOADING & ERROR STATES ============
  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" height={24} />
          </Paper>
          <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 3 }}>
            {[1, 2, 3].map((i) => (
              <ColumnSkeleton key={i} />
            ))}
          </Box>
        </Box>
      </DashboardLayout>
    );
  }
  if (error) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button onClick={() => navigate("/projects")} variant="contained">
            Go to Projects
          </Button>
        </Box>
      </DashboardLayout>
    );
  }
  const effectiveTasks = getEffectiveTasks();
  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        {/* ============ PROJECT SELECTOR BAR ============ */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {/* <Typography variant="h6" fontWeight="600" sx={{ mr: 1 }}>Project:</Typography> */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Select Project</InputLabel>
              <Select
                value={filteredProjects.some(p => String(p.id) === String(id)) ? id : ""}
                label="Select Project"
                onChange={(e) => navigate(`/projects/${e.target.value}/board`)}
                sx={{ borderRadius: 2, bgcolor: "background.paper" }}
              >
                <MenuItem value="">
                  <em>Select a Project</em>
                </MenuItem>
                {filteredProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {hasPermission("project.create") && (
              <Tooltip title="Add Project">
                <IconButton
                  aria-label="add project"
                  size="small"
                  onClick={handleOpenAddProject}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
            {project && (
              <Tooltip title="Edit Project">
                <IconButton
                  aria-label="edit project"
                  size="small"
                  onClick={handleOpenEditProject}
                  disabled={
                    !hasPermission("project.update") &&
                    !hasPermission("project.manage.team") &&
                    !hasPermission("project.update.status")
                  }
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {project && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              Board View for: <strong>{project.name}</strong>
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ============ PROJECT DETAILS PAPER - ADD THIS HERE ============ */}
      {project && (
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ mt: 2, p: 2, borderRadius: 2 }}>
            {/* <Typography variant="h6" gutterBottom>
              Project Details
            </Typography> */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: { xs: "22px", md: "28px" } }}                  
                >
                  {/* <AssignmentIcon color="primary" />  */}
                  {project?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project?.description}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {/* 1. Burndown Toggle */}
                <Tooltip title={showBurndown ? "Hide Burndown" : "Show Burndown"}>
                  <IconButton
                    aria-label="toggle burndown"
                    size="small"
                    onClick={() => setShowBurndown(!showBurndown)}
                    disabled={!isAdmin}
                    color={showBurndown ? "primary" : "default"}
                  >
                    <TrendingDownIcon />
                  </IconButton>
                </Tooltip>

                {/* 2. Attachments */}
                <Tooltip title="Project Attachments">
                  <IconButton
                    aria-label="view attachments"
                    size="small"
                    onClick={() => setAttachmentsDialogOpen(true)}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </Tooltip>

                {/* 3. Sprints */}
                {hasPermission("sprint.view") && (
                  <Tooltip title="Manage Sprints">
                    <IconButton
                      aria-label="view sprints"
                      size="small"
                      onClick={() => navigate(`/projects/${project.id}/sprints`)}
                      disabled={isSaving}
                      color="primary"
                    >
                      <FlagIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {/* 4. Tasks */}
                <Tooltip title="View All Tasks">
                  <IconButton
                    aria-label="view tasks"
                    size="small"
                    onClick={() => navigate(`/tasks?project=${project.id}`)}
                    disabled={isSaving}
                    color="primary"
                  >
                    <TaskIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Collapse in={showBurndown && isAdmin}>
              <BurndownChart
                data={burndownData}
                loading={burndownLoading}
                project={project}
                height={300}
              />
            </Collapse>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">Client:</Typography>
                </Box>
                <Typography variant="body2" sx={{ ml: 3 }}>
                  {project.client?.name || "No Client Assigned"}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">Assigned Users:</Typography>
                </Box>
                {project.assigned_users?.length > 0 ? (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", ml: 3 }}>
                    {project.assigned_users?.map((user) => {
                      const displayName = user.name || user.email?.split("@")[0] || "User";
                      const initial = displayName.charAt(0).toUpperCase();
                      return (
                        <Tooltip key={user.id} title={user.name || user.email || "Unknown User"}>
                          <Avatar
                            sx={{ width: 24, height: 24, fontSize: 10 }}
                            src={getAvatarUrl(user.avatar)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = null;
                            }}
                          >
                            {initial}
                          </Avatar>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                    No users assigned.
                  </Typography>
                )}
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  <Chip
                    icon={<CalendarIcon />}
                    label={`Start: ${new Date(project.start_date).toLocaleDateString("en-US", {
                      day: "numeric", month: "short", year: "numeric",
                    })}`}
                    size="small"
                  />
                  <Chip
                    icon={<CalendarIcon />}
                    label={`End: ${new Date(project.end_date).toLocaleDateString("en-US", {
                      day: "numeric", month: "short", year: "numeric",
                    })}`}
                    size="small"
                  />
                  <Chip
                    label={`${(project.status?.name || "Unknown").toUpperCase()} - ${calculateProjectDuration()}`}
                    sx={{ backgroundColor: project.status?.color || "#9CA3AF", color: "#fff" }}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {/* ============ PLACEHOLDER IF NO PROJECT SELECTED ============ */}
      {!id ? (
        <Box sx={{ textAlign: "center", py: 10, bgcolor: "background.paper", borderRadius: 3, border: "2px dashed", borderColor: "divider" }}>
          <AssignmentIcon sx={{ fontSize: 64, color: "action.disabled", mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>No Project Selected</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Select a project from the dropdown above or create a new one to start managing tasks.
          </Typography>
          {hasPermission("project.create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddProject}
              sx={{ borderRadius: 2, px: 4, py: 1.5 }}
            >
              Create a Project
            </Button>
          )}
        </Box>
      ) : loading ? (
        // ============ LOADING STATE ============
        <Box sx={{ p: 2 }}>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" height={24} />
          </Paper>
          <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 3 }}>
            {[1, 2, 3].map((i) => (
              <ColumnSkeleton key={i} />
            ))}
          </Box>
        </Box>
      ) : error ? (
        // ============ ERROR STATE ============
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button onClick={() => navigate("/projects")} variant="contained">
            Go to Projects
          </Button>
        </Box>
      ) : (
        // ============ KANBAN BOARD ============
        <DragDropContext onDragEnd={onDragEnd}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: isMobile ? "visible" : "auto",
              pb: 3,
              px: { xs: 1, sm: 0 },
              alignItems: isMobile ? "stretch" : "flex-start",
              flexDirection: isMobile ? "column!important" : "row",
              minHeight: "calc(100vh - 280px)",
              width: isMobile ? "100%" : "auto",
              mx: isMobile ? -1 : 0,
              boxSizing: "border-box",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": { height: "6px" },
              "&::-webkit-scrollbar-track": { bgcolor: "grey.100", borderRadius: "4px" },
              "&::-webkit-scrollbar-thumb": { bgcolor: "grey.400", borderRadius: "4px", "&:hover": { bgcolor: "grey.500" } }
            }}
          >
            <Droppable droppableId="columns" type="column" direction="horizontal">
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ display: "flex", gap: 2 }}>
                  {columns.map((column, index) => {
                    const columnTasks = effectiveTasks.filter((task) => {
                      if (String(task.project_id) !== String(id)) return false;
                      const hasMatchingColumn = columns.some(col => col.id === task.effectiveStatusKey);
                      if (!hasMatchingColumn && task.effectiveStatusKey !== "backlog") {
                        return column.id === "backlog";
                      }
                      return task.effectiveStatusKey === column.id;
                    });
                    return (
                      <Draggable key={column.id} draggableId={column.id} index={index} isDragDisabled={!canManageBoard || column.isBacklog}>
                        {(dragProvided, dragSnapshot) => (
                          <Droppable key={column.id} droppableId={column.id}>
                            {(dropProvided, dropSnapshot) => (
                              <Paper
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                elevation={dragSnapshot.isDragging || dropSnapshot.isDraggingOver ? 3 : 1}
                                sx={{
                                  width: isMobile ? "100%" : 280,
                                  minWidth: isMobile ? "100%" : 280,
                                  maxWidth: isMobile ? "100%" : 280,
                                  flex: isMobile ? "1 1 100%" : "0 0 auto",
                                  p: 1.5,
                                  bgcolor: "background.default",
                                  borderRadius: 3,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  transition: "all 0.2s ease",
                                  flex: isMobile ? "none" : "0 0 auto",
                                  display: "flex",
                                  flexDirection: "column",
                                  maxHeight: "calc(100vh - 300px)",
                                  ...(dragSnapshot.isDragging ? { boxShadow: 8, transform: "scale(1.02)", zIndex: 10 } : {}),
                                }}
                              >
                                {/* Column Header */}
                                <Box
                                  {...dragProvided.dragHandleProps}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1.5,
                                    pb: 1,
                                    borderBottom: "2px solid",
                                    borderColor: `${column.color}40`,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        bgcolor: column.color,
                                        boxShadow: column.isBacklog ? "0 0 0 2px #607D8B30" : `0 0 0 2px ${column.color}30`,
                                      }}
                                    />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {column.title}
                                    </Typography>
                                    <Chip
                                      label={columnTasks.length}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: "0.7rem",
                                        bgcolor: `${column.color}20`,
                                        color: column.color,
                                        fontWeight: 600,
                                        "& .MuiChip-label": { px: 1 },
                                      }}
                                    />
                                    {column.isBacklog && (
                                      <Tooltip title="Fixed column - Overdue & unassigned tasks auto-move here">
                                        <LockIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {canManageBoard && !column.isBacklog && (
                                      <>
                                        <Tooltip title="Edit column">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenEditStatus(taskStatuses.find((s) => s.key === column.id));
                                            }}
                                            sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete column">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteStatus(taskStatuses.find((s) => s.key === column.id)?.id);
                                            }}
                                            sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                    {canManageBoard && !column.isBacklog && (
                                      <Tooltip title={`Add task to ${column.title}`}>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const columnStatus = taskStatuses.find((s) => s.key === column.id);
                                            const targetStatusId = columnStatus?.id || backlogStatusId;
                                            setNewTask((prev) => ({ ...prev, status_id: targetStatusId, isBacklog: column.isBacklog }));
                                            setTaskFiles([]);
                                            setAddTaskOpen(true);
                                          }}
                                          sx={{
                                            color: "text.secondary",
                                            "&:hover": { color: "primary.main", bgcolor: "primary.main", color: "white" },
                                          }}
                                        >
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>

                                {/* Tasks */}
                                <Box
                                  ref={dropProvided.innerRef}
                                  {...dropProvided.droppableProps}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    overflowY: "auto",
                                    flex: 1,
                                    pr: 0.5,
                                    "&::-webkit-scrollbar": { width: "4px" },
                                    "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                                    "&::-webkit-scrollbar-thumb": { bgcolor: "action.disabled", borderRadius: "4px", "&:hover": { bgcolor: "action.active" } },
                                  }}
                                >
                                  {columnTasks.map((task, taskIndex) => {
                                    const dueDate = task.due_date ? new Date(task.due_date) : null;
                                    const isOverdue = dueDate && new Date() > dueDate && !["completed", "done"].includes(task.statusObj?.key);
                                    const isAssigned = task.assignedUsers?.some((u) => u.id === authUser?.id);
                                    const isCreator = task.created_by === authUser?.id;
                                    const canEdit = canUpdateTask || canManageTaskTeam || isAssigned || isCreator;
                                    return (
                                      <Draggable key={task.id} draggableId={String(task.id)} index={taskIndex} isDragDisabled={!canManageBoard}>
                                        {(provided, snapshot) => (
                                          <Card
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            elevation={snapshot.isDragging ? 8 : 1}
                                            onClick={() => handleViewDetails(task)}
                                            sx={{
                                              p: 1.5,
                                              cursor: canManageBoard ? "grab" : "pointer",
                                              borderRadius: 2,
                                              overflow: "visible",
                                              border: "1px solid",
                                              borderColor: isOverdue ? "error.light" : "divider",
                                              bgcolor: snapshot.isDragging ? "background.paper" : "background.paper",
                                              transition: "all 0.15s ease",
                                              "&:hover": { borderColor: "primary.main", boxShadow: 2, transform: "translateY(-2px)" },
                                              position: "relative",
                                              "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                left: 0,
                                                top: 8,
                                                bottom: 8,
                                                width: 3,
                                                borderRadius: "0 2px 2px 0",
                                                bgcolor: column.color,
                                                opacity: 0.8,
                                              },
                                            }}
                                          >
                                            <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5 }}>
                                              {(task.priority === "high" || task.priority === "urgent") && (
                                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main", border: "2px solid white", boxShadow: "0 0 0 1px rgba(0,0,0,0.1)" }} />
                                              )}
                                              {isOverdue && (
                                                <Tooltip title="Overdue">
                                                  <ErrorIcon fontSize="small" color="error" sx={{ bgcolor: "white", borderRadius: "50%" }} />
                                                </Tooltip>
                                              )}
                                            </Box>
                                            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                                              <Typography variant="subtitle2" fontWeight="600" sx={{ pr: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: 1.3 }}>
                                                {task.title}
                                              </Typography>
                                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                                                {task.assignedUsers?.slice(0, 3).map((user) => (
                                                  <Tooltip key={user.id} title={user.name}>
                                                    <Avatar
                                                      src={getAvatarUrl(user.avatar)}
                                                      sx={{ width: 22, height: 22, fontSize: 10, border: "2px solid white", boxShadow: "0 0 0 1px rgba(0,0,0,0.1)" }}
                                                    >
                                                      {user.name.charAt(0)}
                                                    </Avatar>
                                                  </Tooltip>
                                                ))}
                                                {task.assignedUsers?.length > 3 && (
                                                  <Typography variant="caption" color="text.secondary">
                                                    +{task.assignedUsers.length - 3}
                                                  </Typography>
                                                )}
                                                {task.sprint_id && (
                                                  <Chip
                                                    label={sprints.find((s) => String(s.id) === String(task.sprint_id))?.name || "Sprint"}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ height: 20, fontSize: "0.65rem" }}
                                                  />
                                                )}
                                              </Box>
                                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                  <AccessTimeIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                                  <Typography variant="caption" color="text.secondary">
                                                    {task.tracked_time?.formatted || "0h"}
                                                  </Typography>
                                                  {task.tracked_time?.is_running && (
                                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "success.main", animation: "pulse 1.5s infinite" }} />
                                                  )}
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                  {task.comment_count > 0 && (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                                      <ChatBubbleOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                                      <Typography variant="caption" color="text.secondary">
                                                        {task.comment_count}
                                                      </Typography>
                                                    </Box>
                                                  )}
                                                  {task.attachments?.length > 0 && (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                                      <AttachFileIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                                      <Typography variant="caption" color="text.secondary">
                                                        {task.attachments.length}
                                                      </Typography>
                                                    </Box>
                                                  )}
                                                </Box>
                                              </Box>
                                              {canEdit && (
                                                <Box sx={{ display: "flex", gap: 0.5, mt: 1, justifyContent: "flex-end" }}>
                                                  <Tooltip title="Edit task">
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEditTask(task); }} sx={{ p: 0.5 }}>
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  {canDeleteTask && (
                                                    <Tooltip title="Delete task">
                                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }} sx={{ p: 0.5, color: "error.main" }}>
                                                        <DeleteIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                </Box>
                                              )}
                                            </CardContent>
                                          </Card>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {dropProvided.placeholder}
                                  {columnTasks.length === 0 && canManageBoard && !column.isBacklog && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 2,
                                        p: 2,
                                        border: "2px dashed",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        textAlign: "center",
                                        color: "text.secondary",
                                        cursor: "pointer",
                                        "&:hover": { borderColor: "primary.main", color: "#fff", bgcolor: "primary.main" },
                                        transition: "all 0.2s ease",
                                      }}
                                      onClick={() => {
                                        const columnStatus = taskStatuses.find((s) => s.key === column.id);
                                        const targetStatusId = columnStatus?.id || backlogStatusId;
                                        setNewTask((prev) => ({ ...prev, status_id: targetStatusId, isBacklog: column.isBacklog }));
                                        setTaskFiles([]);
                                        setAddTaskOpen(true);
                                      }}
                                    >
                                      <AddIcon sx={{ mb: 0.5 }} />
                                      <Typography variant="caption" fontWeight="500">
                                        Add task
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Paper>
                            )}
                          </Droppable>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {canManageBoard && !isMobile && (
                    <Paper
                      elevation={0}
                      onClick={() => setAddStatusOpen(true)}
                      sx={{
                        minWidth: 280,
                        maxWidth: 280,
                        p: 2,
                        borderRadius: 3,
                        border: "2px dashed",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        cursor: "pointer",
                        color: "text.secondary",
                        bgcolor: "transparent",
                        "&:hover": { borderColor: "primary.main", color: "#fff", bgcolor: "primary.main" },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <AddIcon />
                      <Typography variant="subtitle2" fontWeight="500">
                        Add column
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}
            </Droppable>
          </Box>
        </DragDropContext>
      )}
        {/* ============ ADD TASK MODAL ============ */}
        <Dialog
          open={addTaskOpen}
          onClose={() => !isSubmitting && setAddTaskOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Create New Task
            </Typography>
            <IconButton
              onClick={() => setAddTaskOpen(false)}
              disabled={isSubmitting}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <TextField
                label="Task Title *"
                value={newTask.title}
                onChange={(e) =>
                  setNewTask((prev) => ({ ...prev, title: e.target.value }))
                }
                error={!!formErrors.title}
                helperText={formErrors.title}
                required
                fullWidth
                autoFocus
                size="small"
              />
              <TextField
                label="Description"
                value={newTask.description}
                onChange={(e) =>
                  setNewTask((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="Add more details about this task..."
              />
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Column *</InputLabel>
                    <Select
                      value={newTask.status_id || ""}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedStatus = taskStatuses.find((s) => s.id === selectedId);
                        const isBacklog = selectedStatus?.key === "backlog" || selectedStatus?.name?.toLowerCase() === "backlog";
                        setNewTask((prev) => ({
                          ...prev,
                          status_id: selectedId,
                          isBacklog: isBacklog,
                        }));
                      }}
                      label="Column *"
                      error={!!formErrors.status_id}
                    >
                      {taskStatuses.map((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: status.color,
                              }}
                            />
                            {status.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.status_id && (
                      <FormHelperText error>
                        {formErrors.status_id}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sprint</InputLabel>
                    <Select
                      value={newTask.sprint_id || ""}
                      onChange={(e) =>
                        setNewTask((prev) => ({
                          ...prev,
                          sprint_id: e.target.value,
                        }))
                      }
                      label="Sprint"
                    >
                      <MenuItem value="">No Sprint</MenuItem>
                      {sprints.map((sprint) => (
                        <MenuItem key={sprint.id} value={sprint.id}>
                          {sprint.name} ({sprint.status})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={newTask.priority}
                      onChange={(e) =>
                        setNewTask((prev) => ({
                          ...prev,
                          priority: e.target.value,
                        }))
                      }
                      label="Priority"
                    >
                      <MenuItem value="low">
                        <Chip
                          label="Low"
                          size="small"
                          color="success"
                          sx={{ mr: 1 }}
                        />{" "}
                        Low
                      </MenuItem>
                      <MenuItem value="medium">
                        <Chip
                          label="Medium"
                          size="small"
                          color="warning"
                          sx={{ mr: 1 }}
                        />{" "}
                        Medium
                      </MenuItem>
                      <MenuItem value="high">
                        <Chip
                          label="High"
                          size="small"
                          color="error"
                          sx={{ mr: 1 }}
                        />{" "}
                        High
                      </MenuItem>
                      <MenuItem value="urgent">
                        <Chip
                          label="Urgent"
                          size="small"
                          color="error"
                          variant="filled"
                          sx={{ mr: 1, fontWeight: "bold" }}
                        />{" "}
                        Urgent
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Story Points"
                    type="number"
                    value={newTask.story_points}
                    onChange={(e) =>
                      setNewTask((prev) => ({
                        ...prev,
                        story_points: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />
                </Grid>
              </Grid>
              <FormControl fullWidth size="small">
                <InputLabel>Assign To</InputLabel>
                <Select
                  multiple
                  value={newTask.assigned_user_ids}
                  onChange={(e) =>
                    setNewTask((prev) => ({
                      ...prev,
                      assigned_user_ids: e.target.value,
                    }))
                  }
                  label="Assign To"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((userId) => {
                        const user = projectUsers.find((u) => u.id === userId);
                        return user ? (
                          <Chip
                            key={userId}
                            avatar={
                              <Avatar
                                src={getAvatarUrl(user.avatar)}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: 12,
                                }}
                              >
                                {user.name.charAt(0)}
                              </Avatar>
                            }
                            label={user.name}
                            size="small"
                            sx={{ height: 28 }}
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {projectUsers.length > 0 ? (
                    projectUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={getAvatarUrl(user.avatar)}
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 14,
                            }}
                          >
                            {user.name.charAt(0)}
                          </Avatar>
                          {user.name}
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>No users assigned to this project</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <TextField
                label="Due Date"
                type="datetime-local"
                value={newTask.due_date || ""}
                onChange={(e) =>
                  setNewTask((prev) => ({ ...prev, due_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Attachments
                </Typography>
                <Input
                  type="file"
                  inputProps={{ multiple: true, accept: ACCEPTED_MIME_TYPES }}
                  onChange={handleTaskFileChange}
                  fullWidth
                  sx={{
                    "&::file-selector-button": {
                      mr: 2,
                      border: "none",
                      background: theme.palette.primary.main,
                      color: "#fff",
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        background: theme.palette.primary.dark,
                      },
                    },
                  }}
                />
                {taskFiles.length > 0 && (
                  <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {taskFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        size="small"
                        onDelete={() =>
                          setTaskFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        sx={{ height: 28 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1 }}>
            <Button
              onClick={() => setAddTaskOpen(false)}
              disabled={isSubmitting}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              variant="contained"
              disabled={isSubmitting || !newTask.title.trim()}
              sx={{
                minWidth: 100,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                },
              }}
            >
              {isSubmitting ? <CircularProgress size={20} /> : "Create Task"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* ============ EDIT TASK MODAL ============ */}
        {editTaskOpen && editTaskData && (
          <Dialog
            open={editTaskOpen}
            onClose={() => !isSubmitting && setEditTaskOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
            }}
          >
            <DialogTitle
              sx={{
                pb: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Edit Task
              </Typography>
              <IconButton
                onClick={() => setEditTaskOpen(false)}
                disabled={isSubmitting}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <TextField
                  label="Task Title *"
                  value={editTaskData.title || ""}
                  onChange={(e) =>
                    setEditTaskData((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                  autoFocus
                  size="small"
                />
                <TextField
                  label="Description"
                  value={editTaskData.description || ""}
                  onChange={(e) =>
                    setEditTaskData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                />
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Column</InputLabel>
                      <Select
                        value={editTaskData.status_id || ""}
                        onChange={(e) =>
                          setEditTaskData((prev) => ({
                            ...prev,
                            status_id: e.target.value,
                          }))
                        }
                        label="Column"
                      >
                        {taskStatuses.map((status) => (
                          <MenuItem key={status.id} value={status.id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  bgcolor: status.color,
                                }}
                              />
                              {status.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sprint</InputLabel>
                      <Select
                        value={editTaskData.sprint_id || ""}
                        onChange={(e) =>
                          setEditTaskData((prev) => ({
                            ...prev,
                            sprint_id: e.target.value,
                          }))
                        }
                        label="Sprint"
                      >
                        <MenuItem value="">No Sprint</MenuItem>
                        {sprints.map((sprint) => (
                          <MenuItem key={sprint.id} value={sprint.id}>
                            {sprint.name} ({sprint.status})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={editTaskData.priority || "medium"}
                        onChange={(e) =>
                          setEditTaskData((prev) => ({
                            ...prev,
                            priority: e.target.value,
                          }))
                        }
                        label="Priority"
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Story Points"
                      type="number"
                      value={editTaskData.story_points || ""}
                      onChange={(e) =>
                        setEditTaskData((prev) => ({
                          ...prev,
                          story_points: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                    />
                  </Grid>
                </Grid>
                <FormControl fullWidth size="small">
                  <InputLabel>Assign To</InputLabel>
                  <Select
                    multiple
                    value={editTaskData.assigned_user_ids || []}
                    onChange={(e) =>
                      setEditTaskData((prev) => ({
                        ...prev,
                        assigned_user_ids: e.target.value,
                      }))
                    }
                    label="Assign To"
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((userId) => {
                          const user = projectUsers.find(
                            (u) => u.id === userId
                          );
                          return user ? (
                            <Chip
                              key={userId}
                              avatar={
                                <Avatar
                                  src={getAvatarUrl(user.avatar)}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    fontSize: 12,
                                  }}
                                >
                                  {user.name.charAt(0)}
                                </Avatar>
                              }
                              label={user.name}
                              size="small"
                              sx={{ height: 28 }}
                            />
                          ) : null;
                        })}
                      </Box>
                    )}
                  >
                    {projectUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={getAvatarUrl(user.avatar)}
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 14,
                            }}
                          >
                            {user.name.charAt(0)}
                          </Avatar>
                          {user.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Due Date"
                  type="datetime-local"
                  value={
                    editTaskData.due_date
                      ? convertToLocalInput(
                        editTaskData.due_date,
                        company?.timezone
                      )
                      : ""
                  }
                  onChange={(e) =>
                    setEditTaskData((prev) => ({
                      ...prev,
                      due_date: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Attachments
                  </Typography>
                  <Input
                    type="file"
                    inputProps={{ multiple: true, accept: ACCEPTED_MIME_TYPES }}
                    onChange={handleTaskFileChange}
                    fullWidth
                    sx={{
                      "&::file-selector-button": {
                        mr: 2,
                        border: "none",
                        background: theme.palette.primary.main,
                        color: "#fff",
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        "&:hover": {
                          background: theme.palette.primary.dark,
                        },
                      },
                    }}
                  />
                  {taskFiles.length > 0 && (
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {taskFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          size="small"
                          onDelete={() =>
                            setTaskFiles((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          sx={{ height: 28 }}
                        />
                      ))}
                    </Box>
                  )}
                  {attachments.length > 0 && (
                    <List dense sx={{ mt: 1 }}>
                      {attachments.map((att) => (
                        <ListItem key={att.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={att.original_name}
                            secondary={`${(att.size / 1024).toFixed(1)} KB`}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(att)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
              <Button
                onClick={() => setEditTaskOpen(false)}
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateTask}
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  minWidth: 100,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                }}
              >
                {isSubmitting ? <CircularProgress size={20} /> : "Update Task"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {/* ============ ADD/EDIT STATUS MODAL ============ */}
        <Dialog
          open={addStatusOpen || editStatusOpen}
          onClose={() => {
            setAddStatusOpen(false);
            setEditStatusOpen(false);
            setEditStatusData(null);
            setNewStatus({ name: "", key: "", color: "#9CA3AF" });
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {editStatusOpen ? "Edit Column" : "Add Board Column"}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Status Name *"
                value={newStatus.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setNewStatus((prev) => ({
                    ...prev,
                    name,
                    key: name
                      .toLowerCase()
                      .replace(/\s+/g, "_")
                      .replace(/[^a-z0-9_]/g, ""),
                  }));
                }}
                helperText={`Key: ${newStatus.key || "auto-generated"}`}
                required
                fullWidth
                size="small"
              />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  gutterBottom
                  display="block"
                >
                  Column Color
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <input
                    type="color"
                    value={newStatus.color}
                    onChange={(e) =>
                      setNewStatus((prev) => ({
                        ...prev,
                        color: e.target.value,
                      }))
                    }
                    style={{
                      width: 50,
                      height: 40,
                      border: "1px solid #ddd",
                      borderRadius: 4,
                      cursor: "pointer",
                      padding: 2,
                      background: "none",
                    }}
                  />
                  <TextField
                    value={newStatus.color}
                    onChange={(e) =>
                      setNewStatus((prev) => ({
                        ...prev,
                        color: e.target.value,
                      }))
                    }
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="#9CA3AF"
                    inputProps={{
                      maxLength: 7,
                      style: { textTransform: "uppercase" },
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Click the color box to pick or enter hex code
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  gutterBottom
                  display="block"
                >
                  Quick Colors
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}
                >
                  {[
                    "#2196F3",
                    "#4CAF50",
                    "#FF9800",
                    "#F44336",
                    "#9C27B0",
                    "#607D8B",
                    "#E91E63",
                    "#10B981",
                    "#3B82F6",
                    "#EF4444",
                  ].map((color) => (
                    <Box
                      key={color}
                      onClick={() =>
                        setNewStatus((prev) => ({ ...prev, color }))
                      }
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        bgcolor: color,
                        cursor: "pointer",
                        border:
                          newStatus.color === color
                            ? "3px solid #333"
                            : "2px solid transparent",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "scale(1.1)",
                          border: "3px solid #333",
                        },
                      }}
                      title={color}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={() => {
                setAddStatusOpen(false);
                setEditStatusOpen(false);
                setEditStatusData(null);
              }}
              variant="outlined"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            {editStatusOpen && (
              <Button
                onClick={() => handleDeleteStatus(editStatusData?.id)}
                variant="outlined"
                color="error"
                disabled={isSubmitting}
              >
                Delete
              </Button>
            )}
            <Button
              onClick={editStatusOpen ? handleUpdateStatus : handleCreateStatus}
              variant="contained"
              disabled={!newStatus.name.trim() || isSubmitting}
              sx={{
                background: `linear-gradient(135deg, ${newStatus.color} 0%, ${newStatus.color}cc 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${newStatus.color}cc 0%, ${newStatus.color} 100%)`,
                },
                minWidth: 100,
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} />
              ) : editStatusOpen ? (
                "Update Column"
              ) : (
                "Add Column"
              )}
            </Button>
          </DialogActions>
        </Dialog>
        {/* ============ VIEW DETAILS DIALOG ============ */}
        <Dialog
          open={viewDetailsOpen}
          onClose={handleCloseDetails}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              overflow: "hidden",
              bgcolor: "#fafafa",
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 3,
              pb: 2,
              bgcolor: "#fff",
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ maxWidth: "85%" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
              >
                <Chip
                  label={viewingTask?.status?.name || "Unknown"}
                  size="small"
                  sx={{
                    bgcolor: viewingTask?.status?.color || "#e0e0e0",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: 1,
                  }}
                />
                <Chip
                  label={viewingTask?.priority?.toUpperCase() || "MEDIUM"}
                  size="small"
                  variant="outlined"
                  color={
                    viewingTask?.priority === "high" ||
                      viewingTask?.priority === "urgent"
                      ? "error"
                      : viewingTask?.priority === "low"
                        ? "success"
                        : "warning"
                  }
                  sx={{ fontWeight: 600, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="h5" fontWeight="700" color="text.primary">
                {viewingTask?.title}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseDetails}
              sx={{
                bgcolor: "#f5f5f5",
                "&:hover": { bgcolor: "#e0e0e0" },
                mt: 0.5,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, bgcolor: "#fafafa" }}>
            <Grid container spacing={0}>
              <Grid
                item
                size={{ xs: 12, md: 7.5 }}
                sx={{
                  bgcolor: "#fff",
                  borderRight: "1px solid #eee",
                  minHeight: "60vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ p: 3, borderBottom: "1px solid #eee" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <DescriptionIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight="600">
                      Description
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                      minHeight: 40,
                    }}
                  >
                    {viewingTask?.description || "No description provided."}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 3,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <ChatBubbleOutlineIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight="600">
                      Activity & Comments
                    </Typography>
                    <Chip
                      label={taskComments[viewingTask?.id]?.length || 0}
                      size="small"
                      sx={{ ml: 1, bgcolor: "#e0e0e0", height: 20 }}
                    />
                  </Box>
                  <Box sx={{ mb: 3, position: "relative" }}>
                    <MentionsInput
                      key={`comment-task-${viewingTask?.id}`}
                      value={String(newCommentText?.[viewingTask?.id] ?? "")}
                      onChange={(e, newValue) =>
                        setNewCommentText((prev) => ({
                          ...prev,
                          [viewingTask?.id]: newValue,
                        }))
                      }
                      onPaste={(e) =>
                        handlePasteImage(
                          e,
                          viewingTask?.id,
                          setCommentFiles,
                          toast
                        )
                      }
                      placeholder="Write a comment... Type @ to mention (Paste images with Ctrl+V)"
                      markup="@[__display__](__id__)"
                      allowSpaceInQuery
                      style={{
                        control: {
                          backgroundColor: "#fff",
                          fontSize: 14,
                          minHeight: 80,
                          border: "1px solid #ddd",
                          borderRadius: 12,
                          padding: "12px 16px",
                          boxSizing: "border-box",
                          width: "100%",
                          transition: "border 0.2s",
                          "&:focus-within": {
                            borderColor: theme.palette.primary.main,
                            boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                          },
                        },
                        highlighter: { display: "none" },
                        input: {
                          padding: 10,
                          outline: "none",
                          border: "none",
                          width: "100%",
                          backgroundColor: "transparent",
                          color: "inherit",
                          fontFamily: "inherit",
                          fontSize: "inherit",
                        },
                        suggestions: {
                          list: {
                            backgroundColor: "white",
                            border: "1px solid rgba(0,0,0,0.15)",
                            fontSize: 13,
                            maxHeight: 200,
                            overflowY: "auto",
                            borderRadius: 8,
                            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                            zIndex: 9999,
                          },
                          item: {
                            padding: "8px 12px",
                            borderBottom: "1px solid rgba(0,0,0,0.05)",
                            "&focused": { backgroundColor: "#f0f7ff" },
                            cursor: "pointer",
                          },
                        },
                      }}
                    >
                      <Mention
                        trigger="@"
                        markup="@[__display__](__id__)"
                        displayTransform={(id, display) => `@${display}`}
                        data={
                          Array.isArray(projectUsers)
                            ? projectUsers
                              .filter((u) => u && u.name)
                              .map((u) => ({
                                id: String(u.id),
                                display: u.name.trim(),
                              }))
                            : []
                        }
                        appendSpaceOnAdd
                        style={{
                          backgroundColor: `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          padding: "2px 4px",
                          borderRadius: 4,
                        }}
                      />
                    </MentionsInput>
                    {commentFiles[viewingTask?.id] &&
                      Array.from(commentFiles[viewingTask?.id]).length > 0 && (
                        <Box
                          sx={{
                            mt: 1.5,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {Array.from(
                            commentFiles[viewingTask?.id]
                          ).map((file, index) => (
                            <ImagePreview
                              key={index}
                              file={file}
                              index={index}
                              onRemove={(idx) =>
                                removeFile(viewingTask?.id, idx)
                              }
                            />
                          ))}
                        </Box>
                      )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1.5,
                      }}
                    >
                      <Button
                        component="label"
                        variant="text"
                        startIcon={<AttachFileIcon />}
                        size="small"
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "primary.main",
                            bgcolor: "primary.main",
                            color: "#fff",
                          },
                        }}
                      >
                        Attach Files
                        <input
                          type="file"
                          multiple
                          hidden
                          onChange={(e) =>
                            setCommentFiles((prev) => ({
                              ...prev,
                              [viewingTask?.id]: e.target.files,
                            }))
                          }
                        />
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handlePostComment(viewingTask?.id)}
                        disabled={
                          !newCommentText[viewingTask?.id]?.trim() ||
                          !canCreateComment
                        }
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          fontWeight: 600,
                          boxShadow: "none",
                          "&:disabled": { opacity: 0.5 },
                        }}
                      >
                        Post Comment
                      </Button>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      maxHeight: "400px",
                      pr: 1,
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                      "&::-webkit-scrollbar-thumb": {
                        bgcolor: "#ddd",
                        borderRadius: "3px",
                      },
                    }}
                  >
                    {commentLoading[viewingTask?.id] ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : taskComments[viewingTask?.id]?.length > 0 ? (
                      <Stack spacing={2}>
                        {taskComments[viewingTask?.id].map((comment) => (
                          <Box
                            key={comment.id}
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "flex-start",
                              opacity: comment.is_deleted ? 0.6 : 1,
                            }}
                          >
                            <Avatar
                              src={getAvatarUrl(comment.user?.avatar)}
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: 14,
                                flexShrink: 0,
                              }}
                            >
                              {comment.user?.name?.charAt(0)}
                            </Avatar>
                            <Box
                              sx={{
                                flex: 1,
                                bgcolor: "#f5f5f5",
                                borderRadius: 2,
                                p: 2,
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="700"
                                >
                                  {comment.user?.name}
                                  {comment.user_id === authUser?.id && (
                                    <Chip
                                      label="You"
                                      size="small"
                                      sx={{
                                        ml: 1,
                                        height: 16,
                                        fontSize: 10,
                                      }}
                                    />
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatTimeAgo(comment.created_at)}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  lineHeight: 1.6,
                                  mb: 1,
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: highlightMentions(comment.content),
                                }}
                              />
                              {comment.attachments &&
                                comment.attachments.length > 0 && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                      mt: 1,
                                    }}
                                  >
                                    {comment.attachments.map((attachment) =>
                                      attachment.original_name?.match(
                                        /\.(jpg|jpeg|png|gif|webp)$/i
                                      ) ? (
                                        <Box
                                          key={attachment.id}
                                          sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: 1.5,
                                            overflow: "hidden",
                                            border: "1px solid #e0e0e0",
                                            cursor: "pointer",
                                            "&:hover": { opacity: 0.9 },
                                          }}
                                          onClick={() =>
                                            handleImagePreview(attachment)
                                          }
                                        >
                                          <AuthenticatedImage
                                            attachment={attachment}
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <Box
                                          key={attachment.id}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            p: 1,
                                            bgcolor: "#fff",
                                            borderRadius: 1,
                                            border: "1px solid #ddd",
                                            maxWidth: 200,
                                          }}
                                        >
                                          <AttachFileIcon
                                            fontSize="small"
                                            color="action"
                                          />
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ flex: 1 }}
                                          >
                                            {attachment.original_name}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDownload(attachment)
                                            }
                                          >
                                            <DownloadIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                )}
                              {(comment.user_id === authUser?.id ||
                                canDeleteComment) && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteComment(
                                        viewingTask?.id,
                                        comment.id
                                      )
                                    }
                                    sx={{
                                      position: "absolute",
                                      bottom: 8,
                                      right: 8,
                                      color: "text.disabled",
                                      "&:hover": {
                                        color: "error.main",
                                        bgcolor: "error.main",
                                        color: "#fff",
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: "text.secondary",
                        }}
                      >
                        <ChatBubbleOutlineIcon
                          sx={{ fontSize: 40, mb: 1, opacity: 0.3 }}
                        />
                        <Typography>
                          No comments yet. Start the discussion!
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                size={{ xs: 12, md: 4.5 }}
                sx={{
                  bgcolor: "#fafafa",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  maxHeight: "calc(100vh - 100px)",
                  overflowY: "auto",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <PersonIcon fontSize="small" color="action" />
                    <Typography
                      variant="subtitle2"
                      fontWeight="700"
                      color="text.secondary"
                    >
                      ASSIGNEES
                    </Typography>
                  </Box>
                  {viewingTask?.assignedUsers?.length > 0 ? (
                    <Stack spacing={1}>
                      {viewingTask.assignedUsers.map((user) => (
                        <Box
                          key={user.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            bgcolor: "#fff",
                            borderRadius: 2,
                            border: "1px solid #eee",
                          }}
                        >
                          <Avatar
                            src={getAvatarUrl(user.avatar)}
                            sx={{ width: 32, height: 32, fontSize: 14 }}
                          >
                            {user.name?.charAt(0)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="600">
                              {user.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {user.role || "Member"}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      No users assigned.
                    </Typography>
                  )}
                </Box>
                <Divider />
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography
                      variant="subtitle2"
                      fontWeight="700"
                      color="text.secondary"
                    >
                      TIME TRACKING
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      border: "1px solid #eee",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="text.primary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {viewingTask?.tracked_time?.formatted || "00h 00m"}
                    </Typography>
                    <Button
                      variant={isTracking ? "contained" : "outlined"}
                      color={isTracking ? "error" : "success"}
                      startIcon={isTracking ? <StopIcon /> : <PlayArrowIcon />}
                      onClick={isTracking ? stopTimer : startTimer}
                      disabled={!!viewingTask?.tracked_time?.end_time}
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "none",
                      }}
                    >
                      {viewingTask?.tracked_time?.end_time
                        ? "Tracking Ended"
                        : isTracking
                          ? "Stop Timer"
                          : "Start Timer"}
                    </Button>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <AttachFileIcon fontSize="small" color="action" />
                    <Typography
                      variant="subtitle2"
                      fontWeight="700"
                      color="text.secondary"
                    >
                      ATTACHMENTS
                    </Typography>
                    <Chip
                      label={attachments.length}
                      size="small"
                      sx={{ height: 16, fontSize: 10 }}
                    />
                  </Box>
                  {attachments.length > 0 ? (
                    <Stack spacing={1}>
                      {attachments.map((att) => (
                        <Box
                          key={att.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: "#fff",
                            borderRadius: 2,
                            border: "1px solid #eee",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: "primary.main",
                              color: "#fff",
                            },
                            transition: "all 0.2s",
                            cursor: "pointer",
                            group: "attachment",
                          }}
                          onClick={() => {
                            if (
                              att.original_name?.match(
                                /\.(jpg|jpeg|png|gif|webp)$/i
                              )
                            ) {
                              handleImagePreview(att);
                            } else {
                              handleDownload(att);
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: "grey.100",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "grey.600",
                              }}
                            >
                              {att.original_name?.match(
                                /\.(jpg|jpeg|png|gif|webp)$/i
                              ) ? (
                                <img
                                  src={getAttachmentUrl(att)}
                                  alt=""
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: 1,
                                  }}
                                />
                              ) : (
                                <AttachFileIcon fontSize="small" />
                              )}
                            </Box>
                            <Box sx={{ overflow: "hidden" }}>
                              <Typography
                                variant="body2"
                                fontWeight="600"
                                noWrap
                              >
                                {att.original_name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {(att.size / 1024).toFixed(1)} KB
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton size="small" sx={{ color: "inherit" }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      No attachments.
                    </Typography>
                  )}
                </Box>
                <Box sx={{ mt: "auto" }}>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 6 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        gutterBottom
                      >
                        Due Date
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {viewingTask?.due_date
                            ? new Date(
                              viewingTask.due_date
                            ).toLocaleDateString()
                            : "None"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        gutterBottom
                      >
                        Project
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <AssignmentIcon fontSize="small" color="action" />
                        <Typography variant="body2" noWrap>
                          {viewingTask?.project?.name || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
        <ImagePreviewDialog
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          imageUrl={selectedImage.url}
          imageName={selectedImage.name}
          onDownload={handlePreviewDownload}
        />
        <AttachmentsListDialog
          open={attachmentsDialogOpen}
          onClose={() => setAttachmentsDialogOpen(false)}
          attachments={allProjectAttachments}
          tasks={tasks}
          taskComments={taskComments}
          onDownload={handleDownload}
          theme={theme}
        />
        {/* Project Form Dialog - ADD/EDIT PROJECT */}
        <ProjectFormDialog
          open={projectDialogOpen}
          onClose={handleProjectDialogClose}
          dialogType={dialogType}
          formData={projectFormData}
          formErrors={projectFormErrors}
          projects={availableProjects}
          users={allUsers}
          clients={clients}
          projectStatuses={projectStatuses}
          attachments={projectAttachments}
          files={projectFiles}
          setFiles={setProjectFiles}
          isSubmitting={isProjectSubmitting}
          onSubmit={handleProjectSubmit}
          authUser={authUser}
          hasPermission={hasPermission}
          currentProject={currentProjectForEdit}
          company={company}
          handleFormChange={handleProjectFormChange}
          handleUserChange={handleProjectUserChange}
          handleFileChange={handleProjectFileChange}
          handleDownload={handleDownload}
          handleDeleteAttachment={async (attId) => {
            try {
              await attachmentAPI.delete(attId);
              setProjectAttachments((prev) => prev.filter((att) => att.id !== attId));
              toast.success("Attachment deleted");
            } catch (err) {
              console.error("Failed to delete attachment:", err);
              toast.error("Failed to delete attachment");
            }
          }}
          fileInputRef={projectFileInputRef}
          userMap={userMap}
        />
      </Box>
      <style>{`@keyframes pulse { 0% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } }`}</style>
    </DashboardLayout>
  );
};

export default ProjectBoardPage;